/**
 * PostcardEditor — Canva-style live design editor for the 5" × 9" mailer.
 *
 * In-house canvas engine (no konva/fabric): absolutely-positioned DOM elements
 * inside a CSS-scaled 1024 × 576 stage — the SAME coordinate space the server
 * compositor (postcard-compose.js) renders, so a saved design maps 1:1 onto
 * the print asset.
 *
 * UX (Canva conventions): click select · ⌘-click multi-select · marquee
 * select on empty stage · drag move (Shift = axis lock, center snap guides) ·
 * 8-handle resize (single selection) · double-click text edit · layers panel
 * (select anything, even under a full-bleed image; lock/unlock) · align
 * to page / to each other · ⌘D duplicate · ⌘A select all · ⌘Z undo.
 *
 * Design schema (Firestore-ready):
 *   { templateId, accent, canvas:{w,h}, front:[el], back:[el] }
 *   el = { id, type:'text'|'rect'|'image', x, y, w, h, ...type props }
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { blobToPngB64 } from './PostcardQuestionnaire';
import { postcardService } from '../api/postcardService';

export const CANVAS_W = 1024;
export const CANVAS_H = 576;

// MailVotes brand (matches PostcardsPage).
const PRIMARY = '#112030';
const ACCENT_GREEN = '#4CAF50';
const SEL_BLUE = '#3b82f6';
const GUIDE_PINK = '#f43f5e';
const btn = (disabled, bg = PRIMARY) => ({ background: disabled ? '#94a3b8' : bg, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer' });
const btnOutline = { background: 'transparent', color: PRIMARY, border: `1px solid ${PRIMARY}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer' };
const panelBtn = { display: 'block', width: '100%', textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, fontWeight: 600, color: PRIMARY, cursor: 'pointer', marginBottom: 6 };
const label = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 3 };
const sectionLabel = { fontSize: 10.5, fontWeight: 800, letterSpacing: '.09em', color: '#8494a5', display: 'block', marginBottom: 8 };
const smallInput = { width: '100%', padding: '6px 8px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const zoomBtn = { border: 'none', background: '#fff', padding: '6px 11px', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: PRIMARY };

// Hover / focus micro-interactions — inline styles can't express these, so a
// small scoped stylesheet ships with the component (pce- prefix).
const EDITOR_CSS = `
.pce-tile { transition: border-color .13s ease, box-shadow .13s ease, transform .13s ease, background .13s ease; }
.pce-tile:hover { border-color: ${ACCENT_GREEN}; background: #fff; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16,42,67,.10); }
.pce-hbtn { transition: background .12s ease, border-color .12s ease; }
.pce-hbtn:hover { background: #f1f5f9; }
.pce-row { transition: background .1s ease; }
.pce-row:hover { background: #f4f7fa; }
.pce-input { transition: border-color .12s ease, box-shadow .12s ease; }
.pce-input:focus { outline: none; border-color: ${ACCENT_GREEN}; box-shadow: 0 0 0 3px rgba(76,175,80,.14); }
.pce-layers::-webkit-scrollbar { width: 8px; }
.pce-layers::-webkit-scrollbar-thumb { background: #cfd8e3; border-radius: 4px; }
.pce-layers::-webkit-scrollbar-track { background: transparent; }
.pce-genbtn { transition: filter .12s ease, transform .12s ease, box-shadow .12s ease; }
.pce-genbtn:hover { filter: brightness(1.04); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(76,175,80,.22); }
.pce-save { transition: filter .12s ease, box-shadow .12s ease; }
.pce-save:hover { filter: brightness(1.06); box-shadow: 0 4px 14px rgba(76,175,80,.4); }
.pce-chip { transition: background .12s ease, color .12s ease, border-color .12s ease; }
.pce-chip:hover { border-color: ${PRIMARY}; }
.pce-railbtn { transition: background .12s ease, color .12s ease; }
.pce-railbtn:hover { background: rgba(255,255,255,.09); }
.pce-darkbtn { transition: background .12s ease; }
.pce-darkbtn:hover { background: rgba(255,255,255,.12); }
@keyframes pce-rotate { to { transform: rotate(360deg); } }
.pce-spin { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff; border-radius: 50%; animation: pce-rotate .7s linear infinite;
  vertical-align: -2px; margin-right: 7px; }
.pce-spin.dark { border-color: rgba(30,64,175,.25); border-top-color: #1e40af; }
`;

// The WASM background remover locked the whole tab during Brian's live test
// on Firefox: force inference into a worker and cap the wait, so a hang
// degrades into an error message instead of a frozen page.
const removeBgInWorker = async (input) => {
  const { removeBackground } = await import('@imgly/background-removal');
  return await Promise.race([
    removeBackground(input, { proxyToWorker: true }),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Background removal timed out')), 90000)),
  ]);
};
const tinyBtn = (active) => ({ ...btnOutline, flex: 1, padding: '5px 0', fontSize: 12, ...(active ? { background: PRIMARY, color: '#fff' } : {}) });
const iconBtn = (danger) => ({ ...tinyBtn(false), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, ...(danger ? { color: '#b91c1c', borderColor: '#fca5a5' } : {}) });

// ---- inline SVG icons (feather-style, stroke = currentColor, no emoji) -----
const ICON_PATHS = {
  text: <><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></>,
  heading: <><line x1="6" y1="4" x2="6" y2="20" /><line x1="18" y1="4" x2="18" y2="20" /><line x1="6" y1="12" x2="18" y2="12" /></>,
  shape: <rect x="4" y="4" width="16" height="16" rx="2" />,
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  unlock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  up: <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>,
  down: <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
  x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  sparkles: <><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M19 14.5l.9 2.3 2.3.9-2.3.9-.9 2.3-.9-2.3-2.3-.9 2.3-.9z" /><path d="M5 2.5l.7 1.9 1.9.7-1.9.7L5 7.7l-.7-1.9-1.9-.7 1.9-.7z" /></>,
  edit: <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  'check-circle': <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.27" /></>,
  template: <path d="M4 4h16v16H4zM4 10h16M10 10v10" />,
  layers: <path d="M12 2l10 6-10 6L2 8zM2 16l10 6 10-6" />,
  undo: <><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" /></>,
  redo: <><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 15-6.7L21 13" /></>,
  'align-lines': <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" /></>,
};
export const Icon = ({ name, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
    {ICON_PATHS[name]}
  </svg>
);
// ADD-panel tile: icon above label, uniform height (no wrapping, no emoji).
const AddBtn = ({ icon, label: lbl, onClick }) => (
  <button className="pce-tile" onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
    height: 56, background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10,
    fontSize: 12, fontWeight: 600, color: PRIMARY, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap',
  }}>
    <Icon name={icon} size={17} />
    {lbl}
  </button>
);

// Editor text stack ≈ the server's DejaVu/Helvetica print stack (NOT the
// Oswald gallery-preview font) so WYSIWYG tracks the print asset.
const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

// Accent palette — keep in sync with postcard-compose.js accentFor().
const PALETTE = { red: '#a51817', blue: '#1d4ed8', navy: '#1c3d5a', green: '#1c6b3c', purple: '#6b21a8', teal: '#0f766e' };
export function accentFor(party, colorPreference) {
  const pref = String(colorPreference || '').toLowerCase();
  for (const k of Object.keys(PALETTE)) if (pref.includes(k)) return PALETTE[k];
  const p = String(party || '').toUpperCase().charAt(0);
  if (p === 'R') return PALETTE.red;
  if (p === 'D') return PALETTE.blue;
  return PALETTE.navy;
}

const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const clampText = (s, n) => { const t = String(s || '').trim(); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };

// ---- element factories -----------------------------------------------------
let seq = 1;
const nid = () => `el${seq++}_${Math.random().toString(36).slice(2, 7)}`;
const T = (p) => ({ id: nid(), type: 'text', x: 0, y: 0, w: 200, h: 30, text: '', size: 18, weight: 600, color: '#111', align: 'left', ls: 0, lh: 1.2, upper: false, italic: false, ...p });
const R = (p) => ({ id: nid(), type: 'rect', x: 0, y: 0, w: 100, h: 100, fill: '#cccccc', r: 0, opacity: 1, ...p });
const IMG = (p) => ({ id: nid(), type: 'image', x: 0, y: 0, w: 200, h: 200, src: '', fit: 'contain', ...p });

// Postal furniture every back side must carry (stamp box, divider, address
// lines) — geometry copied from the compositor's backSvg.
const mailFurniture = () => ([
  R({ x: 639, y: 132, w: 2, h: 384, fill: '#aebac4' }),
  R({ x: 880, y: 130, w: 88, h: 100, fill: 'transparent', stroke: '#aebac4', strokeW: 2 }),
  T({ x: 880, y: 170, w: 88, h: 18, text: 'STAMP', size: 13, weight: 400, color: '#7d8f9c', align: 'center', ls: 1 }),
  R({ x: 680, y: 319, w: 280, h: 2, fill: '#aebac4' }),
  R({ x: 680, y: 377, w: 280, h: 2, fill: '#aebac4' }),
  R({ x: 680, y: 435, w: 280, h: 2, fill: '#aebac4' }),
]);

// ---- template seeds --------------------------------------------------------
export const EDITOR_TEMPLATES = [
  { id: 'stars', label: 'Stars' },
  { id: 'clean', label: 'Modern' },
  { id: 'bold', label: 'Bold' },
  { id: 'grassroots', label: 'Grassroots' },
  { id: 'flag', label: 'Flag' },
  { id: 'ballot', label: 'Ballot' },
  { id: 'split', label: 'Split' },
];
// Gallery template ids (PostcardTemplates.js) → editor seeds.
const GALLERY_TO_EDITOR = { stars: 'stars', modern: 'clean', bold: 'bold', grassroots: 'grassroots', flag: 'flag', ballot: 'ballot' };

/** Build a fresh front+back element set for a template from order data.
 *  `accentOverride` (editor party/color pickers) wins over the order data. */
export function buildDesign(templateId, d = {}, headshotB64 = null, accentOverride = null) {
  const q = d.questionnaire || {};
  const accent = accentOverride || accentFor(d.party, q.colorPreference);
  const name = (d.candidateName || 'YOUR NAME').toUpperCase();
  const office = (d.office ? `FOR ${d.office}` : 'FOR OFFICE').toUpperCase();
  const lastName = ((d.candidateName || 'Your Name').trim().split(/\s+/).slice(-1)[0]).toUpperCase();
  const message = clampText(d.message || q.keyMessage || 'A message voters will remember', 60);
  const headline = clampText(q.keyMessage || d.message || 'Leadership that puts our community first', 64);
  const body = q.callToAction || "On election day, your voice decides who answers for our community. I'm asking for your vote.";
  const website = q.website || '';
  const disclaimer = clampText(q.disclaimer || `Paid for by Friends of ${titleCase(d.candidateName || 'Your Name')}.`, 90);
  const slogan = d.slogan || 'Leadership you can trust.';
  const headshotEl = (x, y, w, h) => headshotB64
    ? [IMG({ x, y, w, h, src: `data:image/png;base64,${headshotB64}`, role: 'headshot' })]
    : [];

  // Standard back used by clean/split (white, headline + CTA + button).
  const whiteBack = () => [
    R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#ffffff', locked: true, role: 'background' }),
    R({ x: 60, y: 56, w: 150, h: 16, fill: accent }),
    T({ x: 60, y: 100, w: 560, h: 80, text: headline, size: 34, weight: 800, color: '#111111', lh: 1.05 }),
    T({ x: 62, y: 210, w: 540, h: 110, text: body, size: 17, weight: 400, color: '#555555', lh: 1.6 }),
    R({ x: 60, y: 348, w: 260, h: 54, fill: accent, r: 10 }),
    T({ x: 60, y: 364, w: 260, h: 26, text: `VOTE ${lastName}`, size: 21, weight: 800, color: '#ffffff', align: 'center' }),
    ...(website ? [T({ x: 62, y: 500, w: 340, h: 20, text: website, size: 15, weight: 700, color: accent })] : []),
    ...mailFurniture(),
    T({ x: 60, y: 538, w: 600, h: 16, text: disclaimer, size: 12, weight: 400, color: '#7d8f9c' }),
  ];

  if (templateId === 'clean') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#ffffff', locked: true, role: 'background' }),
        R({ x: 0, y: 0, w: 470, h: 576, fill: accent }),
        R({ x: 470, y: 0, w: 14, h: 576, fill: '#c8a24a' }),
        T({ x: 20, y: 186, w: 430, h: 28, text: '★ ★ ★', size: 20, weight: 400, color: '#ffffff', align: 'center' }),
        T({ x: 20, y: 236, w: 430, h: 130, text: name, size: 58, weight: 800, color: '#ffffff', align: 'center', lh: 1.0 }),
        T({ x: 540, y: 200, w: 420, h: 26, text: 'ELECT', size: 20, weight: 700, color: accent, ls: 8 }),
        T({ x: 540, y: 240, w: 430, h: 84, text: office, size: 34, weight: 800, color: '#111111', lh: 1.05 }),
        T({ x: 540, y: 340, w: 420, h: 30, text: slogan, size: 19, weight: 400, italic: true, color: '#666666' }),
      ],
      back: whiteBack(),
    };
  }

  if (templateId === 'bold') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#f4f1e8', locked: true, role: 'background' }),
        R({ x: 0, y: 0, w: 1024, h: 80, fill: accent }),
        R({ x: 0, y: 496, w: 1024, h: 80, fill: '#0a3161' }),
        T({ x: 0, y: 108, w: 1024, h: 30, text: '★ VOTE ★', size: 22, weight: 700, color: accent, align: 'center', ls: 10 }),
        T({ x: 32, y: 158, w: 960, h: 150, text: name, size: 110, weight: 900, color: '#111111', align: 'center', lh: 0.95 }),
        T({ x: 0, y: 330, w: 1024, h: 40, text: office, size: 30, weight: 800, color: '#0a3161', align: 'center', ls: 3 }),
        T({ x: 0, y: 392, w: 1024, h: 30, text: slogan, size: 20, weight: 400, italic: true, color: '#333333', align: 'center' }),
      ],
      back: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#f4f1e8', locked: true, role: 'background' }),
        T({ x: 60, y: 66, w: 560, h: 140, text: headline.toUpperCase(), size: 44, weight: 900, color: accent, lh: 0.98 }),
        T({ x: 62, y: 236, w: 540, h: 110, text: body, size: 17, weight: 400, color: '#3c4a55', lh: 1.6 }),
        R({ x: 60, y: 370, w: 260, h: 54, fill: accent, r: 10 }),
        T({ x: 60, y: 386, w: 260, h: 26, text: `VOTE ${lastName}`, size: 21, weight: 800, color: '#ffffff', align: 'center' }),
        ...(website ? [T({ x: 62, y: 500, w: 340, h: 20, text: website, size: 15, weight: 700, color: accent })] : []),
        ...mailFurniture(),
        T({ x: 60, y: 538, w: 600, h: 16, text: disclaimer, size: 12, weight: 400, color: '#7d8f9c' }),
      ],
    };
  }

  if (templateId === 'grassroots') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: accent, locked: true, role: 'background' }),
        R({ x: 352, y: 68, w: 340, h: 340, fill: '#f4f1e8', r: 999, stroke: '#c8a24a', strokeW: 10 }),
        T({ x: 352, y: 118, w: 340, h: 20, text: '★ ★ ★', size: 14, weight: 400, color: '#1c3d5a', align: 'center' }),
        T({ x: 352, y: 148, w: 340, h: 22, text: 'ELECT', size: 16, weight: 700, color: '#1c3d5a', align: 'center', ls: 8 }),
        T({ x: 382, y: 180, w: 280, h: 90, text: name, size: 42, weight: 800, color: accent, align: 'center', lh: 1.0 }),
        T({ x: 372, y: 288, w: 300, h: 24, text: office, size: 16, weight: 700, color: '#1c3d5a', align: 'center', ls: 3 }),
        T({ x: 0, y: 476, w: CANVAS_W, h: 30, text: slogan.toUpperCase(), size: 20, weight: 600, color: '#ffffff', align: 'center', ls: 6 }),
        T({ x: 0, y: 536, w: CANVAS_W, h: 18, text: disclaimer, size: 12, weight: 400, color: 'rgba(255,255,255,.8)', align: 'center' }),
      ],
      back: whiteBack(),
    };
  }

  if (templateId === 'flag') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#0a3161', locked: true, role: 'background' }),
        R({ x: 0, y: 0, w: CANVAS_W, h: 344, fill: accent }),
        T({ x: 60, y: 52, w: 200, h: 22, text: '★ ★ ★ ★ ★', size: 16, weight: 400, color: '#ffffff' }),
        T({ x: 60, y: 92, w: 400, h: 24, text: 'VOTE FOR', size: 18, weight: 700, color: 'rgba(255,255,255,.9)', ls: 8 }),
        T({ x: 56, y: 126, w: 800, h: 110, text: name, size: 88, weight: 800, color: '#ffffff', lh: 0.95 }),
        T({ x: 60, y: 252, w: 600, h: 32, text: office, size: 24, weight: 700, color: '#ffd98a', ls: 3 }),
        T({ x: 524, y: 496, w: 440, h: 28, text: slogan, size: 20, weight: 400, italic: true, color: '#ffffff', align: 'right' }),
        T({ x: 60, y: 540, w: 600, h: 16, text: disclaimer, size: 12, weight: 400, color: 'rgba(255,255,255,.75)' }),
      ],
      back: whiteBack(),
    };
  }

  if (templateId === 'ballot') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#ffffff', locked: true, role: 'background' }),
        R({ x: 0, y: 0, w: CANVAS_W, h: 92, fill: accent }),
        T({ x: 0, y: 30, w: CANVAS_W, h: 30, text: 'OFFICIAL BALLOT', size: 20, weight: 700, color: '#ffffff', align: 'center', ls: 12 }),
        R({ x: 80, y: 180, w: 120, h: 120, fill: 'transparent', stroke: '#0a3161', strokeW: 10, r: 4 }),
        T({ x: 80, y: 186, w: 120, h: 110, text: '✓', size: 88, weight: 700, color: '#0a3161', align: 'center' }),
        T({ x: 240, y: 186, w: 640, h: 78, text: name, size: 62, weight: 800, color: '#111111', lh: 1.0 }),
        T({ x: 242, y: 272, w: 560, h: 30, text: office, size: 24, weight: 700, color: '#0a3161', ls: 2 }),
        T({ x: 242, y: 336, w: 560, h: 26, text: `★ ${slogan.toUpperCase()} ★`, size: 18, weight: 700, color: accent, ls: 3 }),
        T({ x: 0, y: 538, w: CANVAS_W, h: 16, text: disclaimer, size: 12, weight: 400, color: '#7d8f9c', align: 'center' }),
      ],
      back: whiteBack(),
    };
  }

  if (templateId === 'split') {
    return {
      front: [
        R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#0d1826', locked: true, role: 'background' }),
        R({ x: 512, y: 0, w: 512, h: 576, fill: accent }),
        ...(headshotB64
          ? headshotEl(70, 90, 380, 430)
          : [
            R({ x: 100, y: 96, w: 320, h: 392, fill: 'rgba(255,255,255,0.08)', r: 12 }),
            T({ x: 100, y: 270, w: 320, h: 24, text: 'Your photo here', size: 16, weight: 600, color: 'rgba(255,255,255,0.55)', align: 'center' }),
          ]),
        T({ x: 552, y: 128, w: 430, h: 24, text: 'VOTE FOR', size: 18, weight: 700, color: 'rgba(255,255,255,0.9)', ls: 8 }),
        T({ x: 552, y: 168, w: 440, h: 130, text: name, size: 54, weight: 800, color: '#ffffff', lh: 1.0 }),
        T({ x: 552, y: 316, w: 430, h: 28, text: office, size: 21, weight: 700, color: '#ffd98a', ls: 3 }),
        T({ x: 552, y: 452, w: 430, h: 30, text: slogan, size: 18, weight: 400, italic: true, color: 'rgba(255,255,255,0.9)' }),
      ],
      back: whiteBack(),
    };
  }

  // Default: 'stars' — 1:1 copy of the server compositor's front/back layout.
  const nameW = Math.min(CANVAS_W - 96, Math.max(name.length * 38 + 90, office.length * 15 + 90));
  return {
    front: [
      R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: 'linear-gradient(160deg,#1b2c3f 0%,#0d1826 60%,#0a1420 100%)', locked: true, role: 'background' }),
      R({ x: 0, y: 0, w: 1024, h: 242, fill: 'linear-gradient(180deg, rgba(8,16,26,0.55), rgba(8,16,26,0))' }),
      R({ x: 0, y: 0, w: 1024, h: 10, fill: accent }),
      ...headshotEl(688, 118, 300, 380),
      R({ x: 40, y: 34, w: nameW, h: 126, fill: 'linear-gradient(90deg, rgba(10,18,28,0.92), rgba(10,18,28,0.78))', r: 12 }),
      R({ x: 40, y: 34, w: 8, h: 126, fill: accent, r: 4 }),
      T({ x: 72, y: 50, w: nameW - 60, h: 64, text: name, size: 56, weight: 800, color: '#ffffff', ls: 2, lh: 1.05 }),
      T({ x: 74, y: 116, w: nameW - 90, h: 26, text: office, size: 20, weight: 700, color: '#ffd98a', ls: 5 }),
      T({ x: 40 + nameW - 104, y: 52, w: 90, h: 20, text: '★ ★ ★', size: 16, weight: 400, color: '#ffffff' }),
      R({ x: 0, y: 476, w: 1024, h: 4, fill: 'rgba(255,255,255,0.9)' }),
      R({ x: 0, y: 480, w: 1024, h: 96, fill: accent }),
      T({ x: 52, y: 512, w: 720, h: 44, text: message, size: 32, weight: 800, color: '#ffffff' }),
      T({ x: 884, y: 522, w: 110, h: 24, text: '★ ★ ★', size: 18, weight: 400, color: 'rgba(255,255,255,0.75)' }),
    ],
    back: [
      R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill: '#ffffff', locked: true, role: 'background' }),
      R({ x: 0, y: 0, w: 1024, h: 100, fill: accent }),
      R({ x: 0, y: 100, w: 1024, h: 4, fill: 'rgba(14,26,36,0.25)' }),
      T({ x: 56, y: 24, w: 560, h: 44, text: name, size: 36, weight: 800, color: '#ffffff' }),
      T({ x: 58, y: 66, w: 420, h: 22, text: office, size: 16, weight: 700, color: 'rgba(255,255,255,0.85)', ls: 4 }),
      T({ x: 894, y: 44, w: 110, h: 22, text: '★ ★ ★', size: 16, weight: 400, color: 'rgba(255,255,255,0.85)' }),
      T({ x: 60, y: 150, w: 560, h: 38, text: headline, size: 27, weight: 800, color: '#101d28' }),
      R({ x: 60, y: 192, w: 96, h: 5, fill: accent }),
      T({ x: 62, y: 232, w: 540, h: 90, text: body, size: 17, weight: 400, color: '#3c4a55', lh: 1.6 }),
      R({ x: 60, y: 348, w: 260, h: 54, fill: accent, r: 10 }),
      T({ x: 60, y: 364, w: 260, h: 26, text: `VOTE ${lastName}`, size: 21, weight: 800, color: '#ffffff', align: 'center' }),
      ...(website ? [T({ x: 62, y: 500, w: 340, h: 20, text: website, size: 15, weight: 700, color: accent })] : []),
      ...mailFurniture(),
      T({ x: 60, y: 538, w: 600, h: 16, text: disclaimer, size: 12, weight: 400, color: '#7d8f9c' }),
    ],
  };
}

// ---- background helpers ----------------------------------------------------
// The background is identified by `role: 'background'`, never by array index —
// a reordered layer could sit at index 0 and get destroyed (and then look
// duplicated once the user re-added it). Exactly one background is kept, at
// the bottom of the stack.
const replaceBackground = (list, next) => {
  const rest = list.filter(el => el.role !== 'background');
  return [next, ...rest];
};

// ---- geometry helpers ------------------------------------------------------
const bboxOf = (list) => {
  const x1 = Math.min(...list.map(e => e.x)), y1 = Math.min(...list.map(e => e.y));
  const x2 = Math.max(...list.map(e => e.x + e.w)), y2 = Math.max(...list.map(e => e.y + e.h));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
};
const intersects = (el, m) =>
  !(el.x > m.x + m.w || el.x + el.w < m.x || el.y > m.y + m.h || el.y + el.h < m.y);

// ---- resize handles --------------------------------------------------------
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CURSORS = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize' };
const handleXY = (hd, w, h) => ({
  left: { n: w / 2, s: w / 2, w: 0, e: w, nw: 0, ne: w, sw: 0, se: w }[hd],
  top: { n: 0, s: h, w: h / 2, e: h / 2, nw: 0, ne: 0, sw: h, se: h }[hd],
});

// ---- element renderer ------------------------------------------------------
const textStyle = (el) => ({
  fontFamily: el.font || FONT, fontSize: el.size, fontWeight: el.weight, color: el.color,
  textAlign: el.align, letterSpacing: el.ls, lineHeight: el.lh || 1.2,
  textTransform: el.upper ? 'uppercase' : 'none', fontStyle: el.italic ? 'italic' : 'normal',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
});

const ElView = ({ el, editing, onPointerDown, onDoubleClick, onTextChange, onTextCommit }) => {
  const base = {
    position: 'absolute', left: el.x, top: el.y, width: el.w, height: el.h,
    opacity: el.opacity ?? 1, boxSizing: 'border-box',
    transform: el.rot ? `rotate(${el.rot}deg)` : undefined,
    cursor: el.locked ? 'default' : 'move',
    pointerEvents: el.locked ? 'none' : 'auto',
    userSelect: 'none',
  };
  if (el.type === 'rect') {
    return <div style={{ ...base, background: el.fill, borderRadius: el.r || 0, border: el.strokeW ? `${el.strokeW}px solid ${el.stroke}` : undefined }}
      onPointerDown={(e) => onPointerDown(e, el)} />;
  }
  if (el.type === 'image') {
    return (
      <div style={base} onPointerDown={(e) => onPointerDown(e, el)}>
        {el.src
          ? <img alt="" src={el.src} draggable={false} style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain', pointerEvents: 'none' }} />
          : <div style={{ width: '100%', height: '100%', background: 'rgba(0,0,0,0.06)', border: '1px dashed #94a3b8', borderRadius: 6 }} />}
      </div>
    );
  }
  // text
  if (editing) {
    return (
      <textarea autoFocus value={el.text}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onTextChange(el.id, e.target.value)}
        onBlur={onTextCommit}
        onPointerDown={(e) => e.stopPropagation()}
        style={{ ...base, ...textStyle(el), cursor: 'text', background: 'rgba(59,130,246,0.06)', border: `1px dashed ${SEL_BLUE}`, outline: 'none', resize: 'none', overflow: 'hidden', padding: 0, margin: 0, userSelect: 'text' }} />
    );
  }
  return (
    <div style={{ ...base, ...textStyle(el), overflow: 'visible' }}
      onPointerDown={(e) => onPointerDown(e, el)}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(el); }}>
      {el.text}
    </div>
  );
};

// ---- inspector field helpers -----------------------------------------------
const Num = ({ lbl, value, onChange, onFocus, step = 1, min }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <label style={label}>{lbl}</label>
    <input type="number" className="pce-input" step={step} min={min} value={Math.round(value ?? 0)} onFocus={onFocus}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)} style={smallInput} />
  </div>
);
const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v || '');
const ColorField = ({ lbl, value, onChange, onFocus }) => (
  <div style={{ marginBottom: 8 }}>
    <label style={label}>{lbl}</label>
    <div style={{ display: 'flex', gap: 6 }}>
      <input type="color" value={isHex(value) ? value : '#000000'} onFocus={onFocus}
        onChange={(e) => onChange(e.target.value)} style={{ width: 34, height: 30, padding: 0, border: '1px solid #dee2e6', borderRadius: 6, cursor: 'pointer' }} />
      <input className="pce-input" value={value || ''} onFocus={onFocus} onChange={(e) => onChange(e.target.value)} style={{ ...smallInput, flex: 1 }} />
    </div>
  </div>
);

// One row in the layers panel: type icon/thumbnail + label + lock toggle.
const layerLabel = (el) => {
  if (el.role === 'background') return 'Background';
  if (el.role === 'headshot') return 'Headshot';
  if (el.type === 'text') return clampText(el.text || 'Text', 18);
  if (el.type === 'image') return 'Image';
  return 'Shape';
};
const LayerRow = ({ el, selected, dragOver, onClick, onHover, onToggleLock, onDragStart, onDragOver, onDrop, onDragEnd }) => (
  <div className="pce-row" onClick={onClick}
    onMouseEnter={() => onHover(el.id)} onMouseLeave={() => onHover(null)}
    draggable={el.role !== 'background'}
    onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}
    style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer',
      background: selected ? '#eef2ff' : 'transparent',
      borderLeft: `3px solid ${selected ? SEL_BLUE : 'transparent'}`,
      borderTop: `2px solid ${dragOver ? SEL_BLUE : 'transparent'}`,
      fontSize: 12, color: '#334155',
    }}>
    {/* thumbnail / icon */}
    {el.type === 'image' && el.src
      ? <img alt="" src={el.src} style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4, flexShrink: 0, background: '#eee' }} />
      : el.type === 'rect'
        ? <span style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0, background: el.fill, border: '1px solid #e2e8f0' }} />
        : <span style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0, background: '#f1f5f9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#64748b' }}>T</span>}
    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selected ? 700 : 500 }}>
      {layerLabel(el)}
    </span>
    <button onClick={(e) => { e.stopPropagation(); onToggleLock(el); }} title={el.locked ? 'Unlock' : 'Lock'}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#64748b', display: 'inline-flex', opacity: el.locked ? 1 : 0.35 }}>
      <Icon name={el.locked ? 'lock' : 'unlock'} size={13} />
    </button>
  </div>
);

// ---- main component --------------------------------------------------------
/**
 * Read-only render of one side's element list, scaled to its container.
 * Used by the proof screen to show a saved editor design.
 */
export const DesignPreview = ({ list = [] }) => {
  const boxRef = useRef(null);
  const [s, setS] = useState(0.3);
  useEffect(() => {
    const node = boxRef.current;
    if (!node) return;
    const fit = () => setS((node.clientWidth || CANVAS_W) / CANVAS_W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boxRef} style={{ position: 'relative', width: '100%', aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, overflow: 'hidden', background: '#fff' }}>
      <div style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${s})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0, fontFamily: FONT }}>
        {list.map(el => {
          const b = { position: 'absolute', left: el.x, top: el.y, width: el.w, height: el.h, opacity: el.opacity ?? 1, transform: el.rot ? `rotate(${el.rot}deg)` : undefined };
          if (el.type === 'rect') return <div key={el.id} style={{ ...b, background: el.fill, borderRadius: el.r || 0, border: el.strokeW ? `${el.strokeW}px solid ${el.stroke}` : undefined }} />;
          if (el.type === 'image') return el.src ? <img key={el.id} alt="" src={el.src} style={{ ...b, objectFit: el.fit || 'contain' }} /> : null;
          return <div key={el.id} style={{ ...b, ...textStyle(el), overflow: 'visible' }}>{el.text}</div>;
        })}
      </div>
    </div>
  );
};

/**
 * Build an editable design from an AI proof: each side's rendered JPEG becomes
 * the locked background layer, with the template's typography seeded on top so
 * the copy stays editable. Used by "Edit in studio" on the proof screen.
 */
export function designFromProof(proof, templateId, d = {}, headshotB64 = null, accentOverride = null) {
  const seed = buildDesign(GALLERY_TO_EDITOR[templateId] || templateId || 'stars', d, headshotB64, accentOverride);
  const withArt = (list, b64) => {
    if (!b64) return list;
    const art = IMG({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, src: `data:image/jpeg;base64,${b64}`, fit: 'cover', locked: true, role: 'background' });
    // The proof art replaces the seed background (matched by role, not index).
    return replaceBackground(list, art);
  };
  // Prefer the TEXT-FREE raw art (served alongside newer proofs): seeding the
  // background from the composed proof puts baked-in typography UNDER the live
  // text elements, which ghosts on the server's print render. Old proofs
  // without raw art fall back to the composed image — UNLESS the server
  // watermarked it (unpaid order): baking "PREVIEW" tiles into the design
  // background would survive into the paid print. Seed background instead.
  const fallbackFront = proof?.watermarked ? null : proof?.front;
  const fallbackBack = proof?.watermarked ? null : proof?.back;
  return {
    front: withArt(seed.front, proof?.artFront || fallbackFront),
    back: withArt(seed.back, proof?.artBack || fallbackBack),
  };
}

/**
 * Editable overlay mirroring the server's deterministic stamps on full-AI
 * cards (headline + rule + navy name band). The raw art carries NO text, so
 * every string lives here as a live, draggable element. Geometry matches the
 * server stamps mapped onto the 16:9 canvas (the 4:3 art center-crops ~96px
 * top and bottom under fit:cover, so y positions shift accordingly).
 */
/**
 * Put the artwork back into a server-built AI schema.
 *
 * The stored design references its background as `asset:aiArt` rather than
 * carrying a megabyte of base64 — the server resolves that when it renders.
 * The browser has no asset map, so swap in the real image before editing.
 * Everything else is passed through untouched: this schema is exactly what the
 * preview was rendered from, and rebuilding any of it here is what used to
 * make the editor disagree with the card the customer picked.
 */
export function hydrateAiArt(front, artB64) {
  const src = String(artB64 || '').startsWith('data:') ? artB64 : `data:image/jpeg;base64,${artB64}`;
  return (front || []).map(el =>
    el.src === 'asset:aiArt' ? { ...el, src } : { ...el });
}

export function fullAiOverlay(d = {}, geom = null) {
  const q = d.questionnaire || {};
  const headline = String(d.slogan || q.keyMessage || 'LEADERSHIP THAT DELIVERS').toUpperCase();
  const name = String(d.candidateName || 'YOUR NAME').toUpperCase();
  const office = d.office ? `FOR ${String(d.office).toUpperCase()}` : '';
  // `geom` is the server's measurement of the art these elements land on. The
  // old fixed 89px band was a guess that only held for some themes — measured
  // across the approved deck the painted band runs 40-74px, so a card with a
  // thin band got a slab of navy sitting proud of the artwork. Without a
  // measurement (older drafts) the previous constants still apply.
  const bandH = geom?.bandH || 89;
  const bandTop = geom?.bandTop ?? (CANVAS_H - bandH);
  const bandFill = geom?.bandColor || '#0e264c';
  const bandInk = geom?.bandInk || '#ffffff';
  const headInk = geom?.headlineInk || '#0e264c';
  const accent = geom?.accent || '#f7c846';
  // Keep the office line inside the band whatever height it came back as.
  const nameSize = bandH >= 74 ? 40 : 34;
  const officeY = bandTop + bandH - 22;
  return [
    T({ x: 52, y: 36, w: 520, h: 150, text: headline, size: 44, weight: 800, color: headInk, lh: 1.15 }),
    R({ x: 52, y: 196, w: 110, h: 7, fill: accent }),
    R({ x: 0, y: bandTop, w: CANVAS_W, h: bandH, fill: bandFill }),
    R({ x: 0, y: bandTop, w: CANVAS_W, h: 5, fill: accent }),
    T({ x: 46, y: bandTop + 14, w: 640, h: 48, text: name, size: nameSize, weight: 800, color: bandInk }),
    ...(office ? [T({ x: 46, y: officeY, w: 500, h: 18, text: office, size: 13, weight: 700, color: accent, ls: 4 })] : []),
  ];
}

const PostcardEditor = ({ templateId, data = {}, initialDesign = null, headshotB64 = null, onHeadshot = () => {}, onSave, onCancel }) => {
  const startTpl = GALLERY_TO_EDITOR[templateId] || (EDITOR_TEMPLATES.some(t => t.id === templateId) ? templateId : 'stars');
  const [tpl, setTpl] = useState(initialDesign?.templateId || startTpl);
  const [design, setDesign] = useState(() =>
    initialDesign ? { front: initialDesign.front, back: initialDesign.back } : buildDesign(startTpl, data, headshotB64));
  const [side, setSide] = useState('front');
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [hoverLayerId, setHoverLayerId] = useState(null);
  const [marquee, setMarquee] = useState(null);          // {x,y,w,h} canvas coords
  const [guides, setGuides] = useState({ v: null, h: null }); // snap guide lines
  const [fitScale, setFitScale] = useState(0.55);
  const [zoom, setZoom] = useState(1);                   // user zoom, 1 = fit width
  const [dragOverId, setDragOverId] = useState(null);    // layers drag-reorder target
  const [busyMsg, setBusyMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  // Photo upload: background removal is the default, but per product spec the
  // user can keep the original background (group shots, event photos).
  const [keepPhotoBg, setKeepPhotoBg] = useState(false);
  useEffect(() => {
    if (!errMsg) return undefined;
    const t = setTimeout(() => setErrMsg(''), 7000);
    return () => clearTimeout(t);
  }, [errMsg]);
  // "Generate image" panel (AI image from a prompt via the server's FLUX endpoint)
  const [genPrompt, setGenPrompt] = useState('');
  const [genSize, setGenSize] = useState('1024x1024');   // server-side size whitelist
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);      // base64 of the generated image
  const [genResultSize, setGenResultSize] = useState('1024x1024'); // size the current result was made at
  const [genErr, setGenErr] = useState('');
  // Refine: iterate on the AI card art already on the canvas instead of
  // regenerating the whole design and losing the one they nearly liked.
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineErr, setRefineErr] = useState('');
  const [recents, setRecents] = useState([]);            // last few generations [{b64, size}]
  // Studio chrome (Postcard Studio design)
  const [tab, setTab] = useState('text');                // template|text|shape|image|ai|layers
  const [printGuides, setPrintGuides] = useState(true);  // bleed + safe-area overlay
  const [dirty, setDirty] = useState(false);
  const [savedOnce, setSavedOnce] = useState(!!initialDesign);
  const [defaultFont, setDefaultFont] = useState(null);  // font stack for newly added text
  // Party / accent color pickers (Template tab). Changing them recolors the
  // CURRENT design in place — accent-colored elements swap to the new accent.
  const [partySel, setPartySel] = useState(((data.party || 'R').toUpperCase().charAt(0)) || 'R');
  const [accentSel, setAccentSel] = useState(initialDesign?.accent || accentFor(data.party, data.questionnaire?.colorPreference));
  const scale = Math.min(2.5, fitScale * zoom);

  const designRef = useRef(design);
  const sideRef = useRef(side);
  const scaleRef = useRef(scale);
  const selectedIdsRef = useRef(selectedIds);
  const marqueeRef = useRef(null);
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const clipboardRef = useRef([]);
  const dragIdRef = useRef(null);
  const interactionRef = useRef(null);
  const stageWrapRef = useRef(null);
  const stageBoxRef = useRef(null);
  const imgInputRef = useRef(null);
  const headInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const imgTargetRef = useRef(null); // element id when replacing an image

  useEffect(() => { designRef.current = design; }, [design]);
  useEffect(() => { sideRef.current = side; }, [side]);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  // Fit the 1024×576 stage into the worksurface (width AND height aware —
  // the studio is a full-viewport takeover).
  useEffect(() => {
    const compute = () => {
      const node = stageWrapRef.current;
      const w = (node?.clientWidth || 900) - 90;
      const h = (node?.clientHeight || 600) - 190; // room for floating bars
      setFitScale(Math.max(0.2, Math.min(w / CANVAS_W, h / CANVAS_H, 1.2)));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // The context panel changes width per tab (template = wider) — refit the
  // stage after its width transition settles.
  useEffect(() => {
    const t = setTimeout(() => {
      const node = stageWrapRef.current;
      if (!node) return;
      const w = node.clientWidth - 90, h = node.clientHeight - 190;
      setFitScale(Math.max(0.2, Math.min(w / CANVAS_W, h / CANVAS_H, 1.2)));
    }, 200);
    return () => clearTimeout(t);
  }, [tab]);

  // Full-screen takeover: stop the page behind from scrolling.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ⌘/Ctrl + wheel zooms the canvas (non-passive so preventDefault works).
  useEffect(() => {
    const node = stageWrapRef.current;
    if (!node) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setZoom(z => Math.min(4, Math.max(0.4, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, []);

  const els = design[side];
  const selectedEls = els.filter(e => selectedIds.includes(e.id));
  const single = selectedEls.length === 1 ? selectedEls[0] : null;

  // ---- history / undo / redo ----
  const pushHistory = useCallback(() => {
    historyRef.current.push(JSON.stringify(designRef.current));
    if (historyRef.current.length > 60) historyRef.current.shift();
    redoRef.current = []; // a fresh action invalidates the redo branch
    setDirty(true);
  }, []);
  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (prev) {
      redoRef.current.push(JSON.stringify(designRef.current));
      setDesign(JSON.parse(prev)); setSelectedIds([]); setEditingId(null);
    }
  }, []);
  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (next) {
      historyRef.current.push(JSON.stringify(designRef.current));
      setDesign(JSON.parse(next)); setSelectedIds([]); setEditingId(null);
    }
  }, []);

  // ---- element mutation ----
  const patchEl = useCallback((id, patch) => {
    const s = sideRef.current;
    setDesign(d => ({ ...d, [s]: d[s].map(el => el.id === id ? { ...el, ...patch } : el) }));
  }, []);
  const addEl = (el) => {
    pushHistory();
    setDesign(d => ({ ...d, [side]: [...d[side], el] }));
    setSelectedIds([el.id]);
  };
  const removeSelected = useCallback(() => {
    const s = sideRef.current;
    const ids = selectedIdsRef.current;
    const removable = designRef.current[s].filter(e => ids.includes(e.id) && !e.locked).map(e => e.id);
    if (!removable.length) return;
    pushHistory();
    setDesign(d => ({ ...d, [s]: d[s].filter(e => !removable.includes(e.id)) }));
    setSelectedIds([]);
  }, [pushHistory]);
  const duplicateSelected = useCallback(() => {
    const s = sideRef.current;
    const list = designRef.current[s].filter(e => selectedIdsRef.current.includes(e.id) && !e.locked);
    if (!list.length) return;
    pushHistory();
    const copies = list.map(e => ({ ...e, id: nid(), x: e.x + 14, y: e.y + 14 }));
    setDesign(d => ({ ...d, [s]: [...d[s], ...copies] }));
    setSelectedIds(copies.map(c => c.id));
  }, [pushHistory]);
  const copySel = useCallback(() => {
    const s = sideRef.current;
    const list = designRef.current[s].filter(e => selectedIdsRef.current.includes(e.id) && !e.locked);
    if (list.length) clipboardRef.current = JSON.parse(JSON.stringify(list));
  }, []);
  const pasteClipboard = useCallback(() => {
    const items = clipboardRef.current;
    if (!items.length) return;
    pushHistory();
    const s = sideRef.current;
    const copies = items.map(el => ({ ...el, id: nid(), x: el.x + 14, y: el.y + 14 }));
    setDesign(d => ({ ...d, [s]: [...d[s], ...copies] }));
    setSelectedIds(copies.map(c => c.id));
  }, [pushHistory]);
  const moveLayer = (dir) => {
    if (!single || single.locked) return;
    const arr = [...els];
    const i = arr.findIndex(e => e.id === single.id);
    const j = i + dir;
    // Can't move past the ends, and can't swap with the pinned background.
    if (j < 0 || j >= arr.length || arr[j].role === 'background' || arr[i].role === 'background') return;
    pushHistory();
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setDesign(d => ({ ...d, [side]: arr }));
  };
  // Drag-reorder from the layers panel: drop inserts the dragged layer just
  // above the target row (visually), i.e. after it in array order.
  const reorderLayer = (dragId, targetId) => {
    setDragOverId(null);
    if (!dragId || dragId === targetId) return;
    const s = sideRef.current;
    const arr = [...designRef.current[s]];
    const from = arr.findIndex(x => x.id === dragId);
    const dragged = arr[from];
    const target = arr.find(x => x.id === targetId);
    // The background is pinned to the bottom: it can't be dragged, and nothing
    // can be dropped beneath it.
    if (from < 0 || !target || dragged.role === 'background') return;
    pushHistory();
    arr.splice(from, 1);
    const to = arr.findIndex(x => x.id === targetId);
    const insertAt = Math.max(1, to + 1); // never above index 0 (background)
    arr.splice(insertAt, 0, dragged);
    setDesign(d => ({ ...d, [s]: arr }));
  };
  const toggleLock = (el) => {
    pushHistory();
    patchEl(el.id, { locked: !el.locked });
  };
  // Align: single element → to page; multiple → to each other (selection bbox).
  const alignSel = (mode) => {
    const s = sideRef.current;
    const list = designRef.current[s].filter(e => selectedIdsRef.current.includes(e.id) && !e.locked);
    if (!list.length) return;
    pushHistory();
    const t = list.length > 1 ? bboxOf(list) : { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };
    const patchFor = (el) => ({
      left: { x: t.x }, hcenter: { x: Math.round(t.x + (t.w - el.w) / 2) }, right: { x: t.x + t.w - el.w },
      top: { y: t.y }, vcenter: { y: Math.round(t.y + (t.h - el.h) / 2) }, bottom: { y: t.y + t.h - el.h },
    }[mode]);
    setDesign(d => ({ ...d, [s]: d[s].map(el => list.some(l => l.id === el.id) ? { ...el, ...patchFor(el) } : el) }));
  };

  // ---- drag / resize / marquee engine ----
  const toCanvas = useCallback((clientX, clientY) => {
    const r = stageBoxRef.current.getBoundingClientRect();
    return { x: (clientX - r.left) / scaleRef.current, y: (clientY - r.top) / scaleRef.current };
  }, []);

  const onPointerMove = useCallback((e) => {
    const it = interactionRef.current;
    if (!it) return;
    const sc = scaleRef.current;
    const s = sideRef.current;

    if (it.mode === 'marquee') {
      const cur = toCanvas(e.clientX, e.clientY);
      const m = {
        x: Math.min(it.start.x, cur.x), y: Math.min(it.start.y, cur.y),
        w: Math.abs(cur.x - it.start.x), h: Math.abs(cur.y - it.start.y),
      };
      marqueeRef.current = m;
      setMarquee(m);
      return;
    }

    let dx = (e.clientX - it.sx) / sc;
    let dy = (e.clientY - it.sy) / sc;

    if (it.mode === 'move') {
      if (Math.abs(e.clientX - it.sx) + Math.abs(e.clientY - it.sy) > 4) it.moved = true;
      if (e.shiftKey) { if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0; } // axis lock
      // Snap to canvas edges/center AND other elements' edges/centers, with
      // guide lines (Canva-style). Hold ⌘ to temporarily disable snapping.
      const bb = it.bbox;
      let gv = null, gh = null;
      if (!e.metaKey) {
        const TH = 6;
        const others = designRef.current[s].filter(el => el.role !== 'background' && !it.origs.some(o => o.id === el.id));
        const vT = [0, CANVAS_W / 2, CANVAS_W, ...others.flatMap(el => [el.x, el.x + el.w / 2, el.x + el.w])];
        const hT = [0, CANVAS_H / 2, CANVAS_H, ...others.flatMap(el => [el.y, el.y + el.h / 2, el.y + el.h])];
        const exs = [bb.x + dx, bb.x + dx + bb.w / 2, bb.x + dx + bb.w];
        const eys = [bb.y + dy, bb.y + dy + bb.h / 2, bb.y + dy + bb.h];
        let bx = null, by = null;
        for (const t of vT) for (const p of exs) { const d = t - p; if (Math.abs(d) < TH && (!bx || Math.abs(d) < Math.abs(bx.d))) bx = { d, line: t }; }
        for (const t of hT) for (const p of eys) { const d = t - p; if (Math.abs(d) < TH && (!by || Math.abs(d) < Math.abs(by.d))) by = { d, line: t }; }
        if (bx) { dx += bx.d; gv = bx.line; }
        if (by) { dy += by.d; gh = by.line; }
      }
      setGuides({ v: gv, h: gh });
      const fdx = dx, fdy = dy;
      setDesign(d => ({
        ...d,
        [s]: d[s].map(el => {
          const o = it.origs.find(x => x.id === el.id);
          return o ? { ...el, x: Math.round(o.x + fdx), y: Math.round(o.y + fdy) } : el;
        }),
      }));
      return;
    }

    if (it.mode === 'rotate') {
      const p = toCanvas(e.clientX, e.clientY);
      const o = it.orig;
      const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
      let deg = Math.atan2(p.y - cy, p.x - cx) * 180 / Math.PI - 90; // handle sits below the element
      deg = ((deg % 360) + 360) % 360;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      else { const near = Math.round(deg / 45) * 45; if (Math.abs(deg - near) < 3) deg = near; }
      patchEl(o.id, { rot: Math.round(deg) % 360 });
      return;
    }

    // resize (single selection only)
    const o = it.orig;
    // Rotated elements: work in the element's local frame (rotate the pointer
    // delta back), then keep the center fixed so resizing feels stable.
    if (o.rot) {
      const rad = -o.rot * Math.PI / 180;
      const rdx = dx * Math.cos(rad) - dy * Math.sin(rad);
      const rdy = dx * Math.sin(rad) + dy * Math.cos(rad);
      dx = rdx; dy = rdy;
    }
    let { x, y, w, h } = o;
    let extra = {};
    const hd = it.handle;
    if (hd.includes('e')) w = Math.max(12, o.w + dx);
    if (hd.includes('s')) h = Math.max(12, o.h + dy);
    if (hd.includes('w')) { w = Math.max(12, o.w - dx); x = o.x + (o.w - w); }
    if (hd.includes('n')) { h = Math.max(12, o.h - dy); y = o.y + (o.h - h); }
    // corner-resize keeps aspect for images AND text; text also scales its font
    if ((o.type === 'image' || o.type === 'text') && hd.length === 2 && o.w > 0 && o.h > 0) {
      const ratio = o.w / o.h;
      const nh = w / ratio;
      if (hd.includes('n')) y = y + (h - nh);
      h = nh;
      if (o.type === 'text') {
        const k = w / o.w;
        extra = { size: Math.max(6, Math.round(o.size * k)), ls: Math.round((o.ls || 0) * k * 10) / 10 };
      }
    }
    if (o.rot) { x = o.x + o.w / 2 - w / 2; y = o.y + o.h / 2 - h / 2; }
    patchEl(o.id, { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h), ...extra });
  }, [patchEl, toCanvas]);

  const onPointerUp = useCallback(() => {
    const it = interactionRef.current;
    // Click (no drag) on an already-selected text → inline edit, like Canva.
    if (it?.mode === 'move' && !it.moved && it.editCandidate) {
      setEditingId(it.editCandidate);
    }
    if (it?.mode === 'marquee') {
      const m = marqueeRef.current;
      if (!m || (m.w < 3 && m.h < 3)) {
        setSelectedIds([]); // plain click on empty stage
      } else {
        const hits = designRef.current[sideRef.current].filter(el => !el.locked && intersects(el, m)).map(el => el.id);
        setSelectedIds(hits);
      }
      marqueeRef.current = null;
      setMarquee(null);
    }
    setGuides({ v: null, h: null });
    interactionRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const addWindowListeners = useCallback(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  const onElPointerDown = (e, el) => {
    if (el.locked) return;
    e.stopPropagation();
    e.preventDefault();
    if (editingId) setEditingId(null);
    const cur = selectedIdsRef.current;
    if (e.metaKey || e.ctrlKey) {
      // ⌘-click: toggle membership, no drag.
      setSelectedIds(cur.includes(el.id) ? cur.filter(i => i !== el.id) : [...cur, el.id]);
      return;
    }
    const ids = cur.includes(el.id) ? cur : [el.id];
    setSelectedIds(ids);
    pushHistory();
    const origs = designRef.current[sideRef.current]
      .filter(x => ids.includes(x.id) && !x.locked)
      .map(x => ({ id: x.id, x: x.x, y: x.y, w: x.w, h: x.h }));
    // Canva behavior: clicking an ALREADY-selected text (without dragging)
    // enters edit mode — resolved on pointerup via editCandidate.
    const editCandidate = (cur.length === 1 && cur[0] === el.id && el.type === 'text') ? el.id : null;
    interactionRef.current = { mode: 'move', sx: e.clientX, sy: e.clientY, origs, bbox: bboxOf(origs), editCandidate, moved: false };
    addWindowListeners();
  };
  const startResize = (e, hd) => {
    if (!single || single.locked) return;
    e.stopPropagation();
    e.preventDefault();
    pushHistory();
    interactionRef.current = { mode: 'resize', handle: hd, sx: e.clientX, sy: e.clientY, orig: { ...single } };
    addWindowListeners();
  };
  const startRotate = (e) => {
    if (!single || single.locked) return;
    e.stopPropagation();
    e.preventDefault();
    pushHistory();
    interactionRef.current = { mode: 'rotate', orig: { ...single } };
    addWindowListeners();
  };
  const onStagePointerDown = (e) => {
    if (editingId) setEditingId(null);
    interactionRef.current = { mode: 'marquee', start: toCanvas(e.clientX, e.clientY) };
    marqueeRef.current = null;
    addWindowListeners();
  };

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) {
        if (e.key === 'Escape') e.target.blur();
        return;
      }
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (cmd && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateSelected(); return; }
      if (cmd && e.key.toLowerCase() === 'c') { if (!selectedIdsRef.current.length) return; e.preventDefault(); copySel(); return; }
      if (cmd && e.key.toLowerCase() === 'x') { if (!selectedIdsRef.current.length) return; e.preventDefault(); copySel(); removeSelected(); return; }
      if (cmd && e.key.toLowerCase() === 'v') { e.preventDefault(); pasteClipboard(); return; }
      if (cmd && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedIds(designRef.current[sideRef.current].filter(el => !el.locked).map(el => el.id));
        return;
      }
      if (!selectedIdsRef.current.length) return;
      if (e.key === 'Enter') {
        const s = sideRef.current, ids = selectedIdsRef.current;
        if (ids.length === 1) {
          const el = designRef.current[s].find(x => x.id === ids[0]);
          if (el && el.type === 'text' && !el.locked) { e.preventDefault(); pushHistory(); setEditingId(el.id); return; }
        }
      }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeSelected(); return; }
      if (e.key === 'Escape') { setSelectedIds([]); return; }
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (!d) return;
        const s = sideRef.current;
        const ids = selectedIdsRef.current;
        setDesign(prev => ({
          ...prev,
          [s]: prev[s].map(el => ids.includes(el.id) && !el.locked ? { ...el, x: el.x + d[0], y: el.y + d[1] } : el),
        }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, duplicateSelected, removeSelected, copySel, pasteClipboard, pushHistory]);

  // ---- text editing ----
  const beginTextEdit = (el) => { pushHistory(); setSelectedIds([el.id]); setEditingId(el.id); };
  const commitTextEdit = () => setEditingId(null);

  // ---- image / headshot uploads ----
  const fileToDataUrl = async (file, maxPx = 1600) => `data:image/png;base64,${await blobToPngB64(file, maxPx)}`;
  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const src = await fileToDataUrl(file);
      const targetId = imgTargetRef.current;
      imgTargetRef.current = null;
      if (targetId) { pushHistory(); patchEl(targetId, { src }); }
      else addEl(IMG({ x: 362, y: 158, w: 300, h: 260, src }));
    } catch (err) { console.error('image load failed:', err); }
  };
  const onHeadshotFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusyMsg(keepPhotoBg ? 'Adding photo…' : 'Removing background… (first time downloads the model, ~10–20s)');
    try {
      const source = keepPhotoBg ? file : await removeBgInWorker(file);
      // 1600px cap (not the questionnaire's 800) — this asset feeds print.
      const b64 = await blobToPngB64(source, 1600);
      onHeadshot(b64);
      const src = `data:image/png;base64,${b64}`;
      // Always ADD a new photo element — earlier photos must survive. Cascade
      // the position slightly so consecutive uploads don't stack exactly.
      const n = designRef.current[sideRef.current].filter(el => el.type === 'image').length;
      addEl(IMG({ x: Math.max(20, 688 - n * 24), y: Math.min(160, 118 + n * 18), w: 300, h: 380, src, role: 'headshot' }));
    } catch (err) {
      console.error('background removal failed:', err);
      setBusyMsg('');
      // Inline error, not window.alert — a blocking dialog on top of a busy
      // WASM tab is exactly what read as a crash in Brian's test.
      setErrMsg('Could not process the photo — try a different image.');
      return;
    }
    setBusyMsg('');
  };
  const onBgFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const src = await fileToDataUrl(file, 2048);
      pushHistory();
      setDesign(d => ({ ...d, [side]: replaceBackground(d[side],
        IMG({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, src, fit: 'cover', locked: true, role: 'background' })) }));
    } catch (err) { console.error('background load failed:', err); }
  };
  const setBgFill = (fill) => {
    pushHistory();
    setDesign(d => ({ ...d, [side]: replaceBackground(d[side],
      R({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H, fill, locked: true, role: 'background' })) }));
  };

  // ---- AI image generation ----
  const runGenerate = async () => {
    const p = genPrompt.trim();
    if (!p || genBusy) return;
    setGenBusy(true); setGenErr(''); setGenResult(null);
    try {
      const d = await postcardService.generateImage(p, genSize);
      setGenResult(d.imageB64);
      setGenResultSize(genSize);
      setRecents(r => [{ b64: d.imageB64, size: genSize }, ...r].slice(0, 4));
    } catch (e) {
      setGenErr(e.message === 'No authenticated user' ? 'Sign in to generate images.' : e.message);
    } finally { setGenBusy(false); }
  };
  /**
   * Refine the card's AI background in place. Only the background image is
   * sent and replaced — every text and shape element the customer has already
   * moved or retyped stays exactly where it is, which is the whole point of
   * refining rather than regenerating.
   *
   * The returned geometry re-seats the band and re-picks the ink, because a
   * refine can change the art's colours (a "warmer background" that turns the
   * band pale would leave white text unreadable on it).
   */
  const runRefine = async () => {
    const instruction = refinePrompt.trim();
    if (!instruction || refineBusy) return;
    const bg = design.front.find(el => el.role === 'background' && el.type === 'image' && el.src);
    if (!bg) { setRefineErr('This design has no AI artwork to refine.'); return; }
    setRefineBusy(true); setRefineErr('');
    try {
      const b64 = String(bg.src).split(',', 2)[1] || '';
      const d = await postcardService.refineArt(b64, instruction);
      const nextSrc = `data:image/jpeg;base64,${d.artB64}`;
      setDesign(prev => ({
        ...prev,
        front: prev.front.map((el) => {
          if (el.id === bg.id) return { ...el, src: nextSrc };
          if (!d.geometry) return el;
          // Re-seat the band and its strings onto the refined art.
          if (el.type === 'rect' && el.h === 5) return { ...el, y: d.geometry.bandTop, fill: d.geometry.accent };
          if (el.type === 'rect' && el.w === CANVAS_W && el.h > 20) {
            return { ...el, y: d.geometry.bandTop, h: d.geometry.bandH, fill: d.geometry.bandColor };
          }
          return el;
        }),
      }));
      setRefinePrompt('');
    } catch (e) {
      setRefineErr(e.message === 'No authenticated user' ? 'Sign in to refine.' : e.message);
    } finally { setRefineBusy(false); }
  };

  // Insert dimensions follow the generated aspect ratio, centered on canvas.
  const genDims = (size) => size === '1024x576' ? { w: 480, h: 270 } : size === '576x1024' ? { w: 250, h: 444 } : { w: 360, h: 360 };
  const genBox = (size) => { const d = genDims(size); return { x: Math.round((CANVAS_W - d.w) / 2), y: Math.max(20, Math.round((CANVAS_H - d.h) / 2)), ...d }; };
  const addGenerated = () => {
    if (!genResult) return;
    addEl(IMG({ ...genBox(genResultSize), src: `data:image/png;base64,${genResult}` }));
  };
  const addGeneratedCutout = async () => {
    if (!genResult || genBusy) return;
    setGenBusy(true); setGenErr('');
    setBusyMsg('Removing background… (first time downloads the model, ~10–20s)');
    try {
      const blob = await (await fetch(`data:image/png;base64,${genResult}`)).blob();
      const cut = await removeBgInWorker(blob);
      const b64 = await blobToPngB64(cut, 1600);
      addEl(IMG({ ...genBox(genResultSize), src: `data:image/png;base64,${b64}` }));
    } catch (e) {
      console.error('cutout failed:', e);
      setGenErr('Background removal failed — try "Add to card" instead.');
    } finally { setGenBusy(false); setBusyMsg(''); }
  };

  const switchTemplate = (id) => {
    pushHistory();
    setTpl(id);
    setDesign(buildDesign(id, data, headshotB64, accentSel));
    setSelectedIds([]);
    setEditingId(null);
  };

  const save = () => {
    setDirty(false);
    setSavedOnce(true);
    onSave && onSave({
      templateId: tpl,
      accent: accentSel,
      party: partySel,
      canvas: { w: CANVAS_W, h: CANVAS_H },
      front: design.front,
      back: design.back,
    });
  };

  // ---- studio panel helpers ----
  const accent = accentSel;
  // Swap every accent-colored fill/stroke/text to the new accent, both sides.
  const applyAccent = (next) => {
    if (next === accentSel) return;
    const prev = accentSel;
    pushHistory();
    const recolor = (el) => ({
      ...el,
      ...(el.fill === prev ? { fill: next } : {}),
      ...(el.stroke === prev ? { stroke: next } : {}),
      ...(el.color === prev ? { color: next } : {}),
    });
    setDesign(d => ({ front: d.front.map(recolor), back: d.back.map(recolor) }));
    setAccentSel(next);
  };
  const pickParty = (p) => {
    setPartySel(p);
    applyAccent(p === 'R' ? PALETTE.red : p === 'D' ? PALETTE.blue : PALETTE.navy);
  };
  const addTextPreset = (kind) => {
    const presets = {
      heading: T({ x: 262, y: 210, w: 500, h: 66, text: 'Add a heading', size: 52, weight: 900, color: '#111111', align: 'center', font: defaultFont || undefined }),
      sub: T({ x: 312, y: 250, w: 400, h: 36, text: 'Add a subheading', size: 27, weight: 700, color: '#111111', align: 'center', font: defaultFont || undefined }),
      body: T({ x: 342, y: 268, w: 340, h: 44, text: 'Add body text', size: 16, weight: 400, color: '#333333', align: 'center', lh: 1.5, font: defaultFont || undefined }),
    };
    addEl(presets[kind]);
  };
  // Campaign fields insert live order data as editable text elements.
  const insertField = (key) => {
    const q = data.questionnaire || {};
    const vals = {
      'Candidate name': { text: (data.candidateName || 'Your Name').toUpperCase(), size: 44, weight: 800 },
      Office: { text: (data.office ? `FOR ${data.office}` : 'FOR OFFICE').toUpperCase(), size: 22, weight: 700, ls: 3 },
      Slogan: { text: data.slogan || 'Leadership you can trust.', size: 20, weight: 400, italic: true },
      Website: { text: q.website || 'yourname.com', size: 16, weight: 700, color: accent },
      Disclaimer: { text: q.disclaimer || `Paid for by Friends of ${titleCase(data.candidateName || 'Your Name')}.`, size: 12, weight: 400, color: '#7d8f9c' },
    };
    const v = vals[key];
    if (!v) return;
    addEl(T({ x: 292, y: 250, w: 440, h: Math.round((v.size || 18) * 1.3), color: '#111111', align: 'center', font: defaultFont || undefined, ...v }));
  };
  const addShape = (kind) => {
    const shapes = {
      square: R({ x: 422, y: 198, w: 180, h: 180, fill: accent, r: 8 }),
      circle: R({ x: 432, y: 208, w: 160, h: 160, fill: accent, r: 999 }),
      line: R({ x: 362, y: 284, w: 300, h: 6, fill: accent, r: 3 }),
      star: T({ x: 472, y: 248, w: 80, h: 80, text: '★', size: 64, weight: 400, color: accent, align: 'center' }),
      stars: T({ x: 442, y: 264, w: 140, h: 30, text: '★ ★ ★', size: 22, weight: 400, color: accent, align: 'center' }),
      frame: R({ x: 402, y: 218, w: 220, h: 140, fill: 'transparent', stroke: accent, strokeW: 4, r: 6 }),
    };
    addEl(shapes[kind]);
  };
  // Brand swatch → recolor the selection (rect fill / text color).
  const applySwatch = (c) => {
    const list = selectedEls.filter(e => !e.locked);
    if (!list.length) return;
    pushHistory();
    const s = sideRef.current;
    setDesign(d => ({ ...d, [s]: d[s].map(el => list.some(l => l.id === el.id)
      ? { ...el, ...(el.type === 'rect' ? { fill: c } : el.type === 'text' ? { color: c } : {}) }
      : el) }));
  };
  const setSelFont = (stack) => {
    setDefaultFont(stack);
    if (single && single.type === 'text') { pushHistory(); patchEl(single.id, { font: stack }); }
  };
  const FONT_CHOICES = [
    { label: 'Helvetica', stack: "'Helvetica Neue', Helvetica, Arial, sans-serif", note: 'Print standard' },
    { label: 'Georgia', stack: "Georgia, 'Times New Roman', serif", note: 'Serif' },
    { label: 'Oswald', stack: "'Oswald', 'Arial Narrow', sans-serif", note: 'Campaign' },
    { label: 'Courier', stack: "'Courier New', monospace", note: 'Typewriter' },
  ];

  const bg = els.find(el => el.role === 'background') || els[0];
  const hoverEl = hoverLayerId ? els.find(e => e.id === hoverLayerId) : null;
  const groupBox = selectedEls.length > 1 ? bboxOf(selectedEls) : null;
  const hs = 10 / scale;       // handle size in stage units → constant on screen
  const bw = 1.5 / scale;      // selection border width
  const canUndo = historyRef.current.length > 0;
  const canRedo = redoRef.current.length > 0;

  // Rail tabs (Postcard Studio design).
  const RAIL = [
    { key: 'template', label: 'Template', icon: 'template' },
    { key: 'text', label: 'Text', icon: 'text' },
    { key: 'shape', label: 'Shape', icon: 'shape' },
    { key: 'image', label: 'Image', icon: 'image' },
    { key: 'ai', label: 'AI', icon: 'sparkles' },
    { key: 'layers', label: 'Layers', icon: 'layers' },
  ];
  const TPL_CARDS = [
    { id: 'stars', label: 'Stars', bg: 'linear-gradient(160deg,#1b2c3f,#0a1420)', bar: accent, band: accent, nameColor: '#fff' },
    { id: 'clean', label: 'Modern', bg: '#ffffff', bar: '#c8a24a', band: '#f1f5f9', nameColor: accent },
    { id: 'bold', label: 'Bold', bg: '#f4f1e8', bar: accent, band: '#0a3161', nameColor: '#111' },
    { id: 'grassroots', label: 'Grassroots', bg: accent, bar: 'transparent', band: 'transparent', nameColor: accent, circle: true },
    { id: 'flag', label: 'Flag', bg: `linear-gradient(180deg, ${accent} 60%, #0a3161 60%)`, bar: 'transparent', band: 'transparent', nameColor: '#fff' },
    { id: 'ballot', label: 'Ballot', bg: '#ffffff', bar: accent, band: '#f1f5f9', nameColor: '#111', check: true },
    { id: 'split', label: 'Split', bg: `linear-gradient(90deg,#0d1826 50%, ${accent} 50%)`, bar: 'transparent', band: 'transparent', nameColor: '#fff' },
  ];
  const PARTY_OPTS = [['R', 'Republican', PALETTE.red], ['D', 'Democrat', PALETTE.blue], ['I', 'Independent', PALETTE.navy]];
  const cardName = (data.candidateName || 'JANE SMITH').toUpperCase();
  const SWATCHES = [PALETTE.red, PALETTE.blue, PALETTE.navy, PALETTE.green, '#ffd98a', '#ffffff'];
  const cycleAlign = () => {
    if (!single) return;
    const next = { left: 'center', center: 'right', right: 'left' }[single.align || 'left'];
    pushHistory();
    patchEl(single.id, { align: next });
  };
  const toolIcon = (name, onClick, { title, color = '#64748b', active = false } = {}) => (
    <button className="pce-hbtn" onClick={onClick} title={title}
      style={{ width: 28, height: 28, border: 'none', background: active ? '#eef2f6' : 'transparent', borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color, cursor: 'pointer', padding: 0 }}>
      <Icon name={name} size={15} />
    </button>
  );
  const toolDivider = <span style={{ width: 1, height: 20, background: '#eef2f6' }} />;
  // Tiny live preview of one side for the page switcher.
  const MiniSide = ({ list }) => (
    <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative', overflow: 'hidden', background: '#fff' }}>
      <div style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${104 / CANVAS_W})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0, pointerEvents: 'none', fontFamily: FONT }}>
        {list.map(el => {
          const b = { position: 'absolute', left: el.x, top: el.y, width: el.w, height: el.h, opacity: el.opacity ?? 1, transform: el.rot ? `rotate(${el.rot}deg)` : undefined };
          if (el.type === 'rect') return <div key={el.id} style={{ ...b, background: el.fill, borderRadius: el.r || 0, border: el.strokeW ? `${el.strokeW}px solid ${el.stroke}` : undefined }} />;
          if (el.type === 'image') return el.src ? <img key={el.id} alt="" src={el.src} style={{ ...b, objectFit: el.fit || 'contain' }} /> : null;
          return <div key={el.id} style={{ ...b, ...textStyle(el) }}>{el.text}</div>;
        })}
      </div>
    </div>
  );

  const railBtn = (t) => (
    <button key={t.key} className="pce-railbtn" onClick={() => setTab(t.key)}
      style={{
        width: 60, height: 56, border: 'none', borderRadius: 10, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer',
        color: tab === t.key ? PRIMARY : (t.key === 'ai' ? '#7fe08a' : 'rgba(255,255,255,.65)'),
        background: tab === t.key ? '#fff' : 'transparent',
      }}>
      <Icon name={t.icon} size={19} />
      <span style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</span>
    </button>
  );

  const panelCard = { border: '1px solid #e5eaf0', borderRadius: 12, padding: 14, background: '#fff' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#e9eef4' }}>
      <style>{EDITOR_CSS}</style>

      {/* ══ top bar ══ */}
      <div style={{ height: 56, background: PRIMARY, display: 'flex', alignItems: 'center', gap: 14, padding: '0 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/mailvotes-logo.png" alt="MailVotes" style={{ height: 30, width: 'auto' }} />
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>MailVotes</span>
          <span style={{ color: 'rgba(255,255,255,.45)', fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap', marginLeft: -4 }}>by Doornoc</span>
        </div>
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,.14)', flexShrink: 0 }} />
        <span style={{ color: 'rgba(255,255,255,.75)', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, maxWidth: 220 }}>
          {(data.candidateName || 'Your Name')} — 5" × 9" Mailer
        </span>
        <div style={{ display: 'flex', gap: 2 }}>
          <button className="pce-darkbtn" onClick={undo} title="Undo (⌘Z)"
            style={{ width: 30, height: 30, border: 'none', background: 'transparent', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: canUndo ? 'rgba(255,255,255,.8)' : 'rgba(255,255,255,.3)', cursor: 'pointer' }}>
            <Icon name="undo" size={15} />
          </button>
          <button className="pce-darkbtn" onClick={redo} title="Redo (⌘⇧Z)"
            style={{ width: 30, height: 30, border: 'none', background: 'transparent', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: canRedo ? 'rgba(255,255,255,.8)' : 'rgba(255,255,255,.3)', cursor: 'pointer' }}>
            <Icon name="redo" size={15} />
          </button>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', background: 'rgba(255,255,255,.1)', borderRadius: 10, padding: 3 }}>
          {['front', 'back'].map(s => (
            <button key={s} onClick={() => { setSide(s); setSelectedIds([]); setEditingId(null); }}
              style={{
                border: 'none', padding: '5px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer', textTransform: 'capitalize',
                background: side === s ? '#fff' : 'transparent',
                color: side === s ? PRIMARY : 'rgba(255,255,255,.7)',
              }}>{s}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <button onClick={() => setPrintGuides(g => !g)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
          <span style={{ width: 13, height: 13, border: `1.5px dashed ${GUIDE_PINK}`, borderRadius: 3 }} />
          Print guides
          <span style={{ width: 26, height: 15, background: printGuides ? ACCENT_GREEN : 'rgba(255,255,255,.25)', borderRadius: 999, position: 'relative', transition: 'background .15s' }}>
            <span style={{ position: 'absolute', top: 1.5, left: printGuides ? 12 : 2, width: 12, height: 12, background: '#fff', borderRadius: '50%', transition: 'left .15s' }} />
          </span>
        </button>
        {savedOnce && !dirty && (
          <span style={{ color: 'rgba(255,255,255,.55)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="check-circle" size={13} /> Saved
          </span>
        )}
        <button onClick={onCancel}
          style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.85)', background: 'transparent', border: '1px solid rgba(255,255,255,.25)', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
        <button className="pce-save" onClick={save}
          style={{ padding: '8px 20px', fontSize: 13, fontWeight: 700, color: '#fff', background: ACCENT_GREEN, border: 'none', borderRadius: 8, cursor: 'pointer', boxShadow: '0 2px 10px rgba(76,175,80,.4)' }}>Save design</button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* ══ icon rail ══ */}
        <div style={{ width: 72, background: '#0c1824', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 4, flexShrink: 0 }}>
          {RAIL.map(railBtn)}
        </div>

        {/* ══ context panel ══ */}
        <div style={{ width: tab === 'template' ? 384 : 280, background: '#fff', borderRight: '1px solid #e5eaf0', padding: 16, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 13, overflowY: 'auto', boxSizing: 'border-box', transition: 'width .15s ease' }} className="pce-layers">

          {tab === 'template' && (<>
            <div style={sectionLabel}>PARTY</div>
            <div style={{ display: 'flex', background: '#eef2f6', borderRadius: 10, padding: 3 }}>
              {PARTY_OPTS.map(([k, lbl, c]) => (
                <button key={k} onClick={() => pickParty(k)}
                  style={{
                    flex: 1, border: 'none', padding: '6px 4px', fontSize: 11.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    background: partySel === k ? '#fff' : 'transparent',
                    color: partySel === k ? PRIMARY : '#64748b',
                    boxShadow: partySel === k ? '0 1px 4px rgba(16,42,67,.12)' : 'none',
                    transition: 'background .12s ease',
                  }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: c, flexShrink: 0 }} />{lbl}
                </button>
              ))}
            </div>
            <div style={{ ...sectionLabel, marginTop: 2 }}>ACCENT COLOR</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {Object.values(PALETTE).map(c => (
                <span key={c} onClick={() => applyAccent(c)} title={c}
                  style={{
                    width: 32, height: 32, borderRadius: 9, background: c, cursor: 'pointer',
                    border: '3px solid #fff',
                    boxShadow: accentSel === c ? `0 0 0 2px ${c}, 0 2px 6px rgba(16,42,67,.3)` : '0 1px 3px rgba(16,42,67,.18)',
                  }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5, marginTop: -4 }}>Changing party or color recolors the current design in place.</div>
            <div style={{ ...sectionLabel, marginTop: 2 }}>TEMPLATES</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {TPL_CARDS.map(t => (
                <div key={t.id} className="pce-tile" onClick={() => switchTemplate(t.id)}
                  style={{ border: `2px solid ${tpl === t.id ? ACCENT_GREEN : '#e5eaf0'}`, borderRadius: 12, padding: 6, cursor: 'pointer', background: '#fff' }}>
                  <div style={{ aspectRatio: '9 / 5', borderRadius: 6, background: t.bg, position: 'relative', overflow: 'hidden' }}>
                    <span style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '10%', background: t.bar }} />
                    {t.circle
                      ? <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '54%', aspectRatio: '1', borderRadius: '50%', background: '#f4f1e8', border: '2px solid #c8a24a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7.5, fontWeight: 900, color: t.nameColor, textAlign: 'center', padding: 2, boxSizing: 'border-box' }}>{cardName}</span>
                      : <span style={{ position: 'absolute', left: '7%', top: '20%', fontSize: 10.5, fontWeight: 900, color: t.nameColor, whiteSpace: 'nowrap' }}>{t.check ? '✓ ' : ''}{cardName}</span>}
                    <span style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: '18%', background: t.band }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 2px 0' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: PRIMARY }}>{t.label}</span>
                    {tpl === t.id && <span style={{ fontSize: 9.5, fontWeight: 700, color: ACCENT_GREEN }}>SELECTED</span>}
                  </div>
                </div>
              ))}
            </div>
          </>)}

          {tab === 'text' && (<>
            <div style={sectionLabel}>ADD TEXT</div>
            <div className="pce-tile" onClick={() => addTextPreset('heading')} style={{ border: '1px solid #e5eaf0', borderRadius: 10, padding: '14px 16px', background: '#f8fafc', cursor: 'pointer' }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: PRIMARY, letterSpacing: '.02em' }}>Add a heading</div>
            </div>
            <div className="pce-tile" onClick={() => addTextPreset('sub')} style={{ border: '1px solid #e5eaf0', borderRadius: 10, padding: '12px 16px', background: '#f8fafc', cursor: 'pointer' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: PRIMARY }}>Add a subheading</div>
            </div>
            <div className="pce-tile" onClick={() => addTextPreset('body')} style={{ border: '1px solid #e5eaf0', borderRadius: 10, padding: '11px 16px', background: '#f8fafc', cursor: 'pointer' }}>
              <div style={{ fontSize: 13, color: '#475569' }}>Add body text</div>
            </div>
            <div style={{ ...sectionLabel, marginTop: 4 }}>FONTS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {FONT_CHOICES.map(f => {
                const active = (single?.type === 'text' ? (single.font || FONT) : (defaultFont || FONT)) === f.stack;
                return (
                  <div key={f.label} className="pce-row" onClick={() => setSelFont(f.stack)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${active ? ACCENT_GREEN : '#dfe6ee'}`, background: active ? '#f0fdf4' : '#fff', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
                    <span style={{ fontSize: 14, color: PRIMARY, fontFamily: f.stack, fontWeight: 700 }}>{f.label}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>{f.note}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ ...sectionLabel, marginTop: 4 }}>CAMPAIGN FIELDS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['Candidate name', 'Office', 'Slogan', 'Website', 'Disclaimer'].map(c => (
                <span key={c} className="pce-hbtn" onClick={() => insertField(c)}
                  style={{ fontSize: 12, fontWeight: 600, color: PRIMARY, background: '#eef2f6', border: '1px solid #dfe6ee', borderRadius: 999, padding: '5px 12px', cursor: 'pointer' }}>{c}</span>
              ))}
            </div>
          </>)}

          {tab === 'shape' && (<>
            <div style={sectionLabel}>ADD SHAPE</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div className="pce-tile" onClick={() => addShape('square')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><span style={{ width: 26, height: 26, background: PRIMARY, borderRadius: 3 }} /></div>
              <div className="pce-tile" onClick={() => addShape('circle')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><span style={{ width: 26, height: 26, background: PRIMARY, borderRadius: '50%' }} /></div>
              <div className="pce-tile" onClick={() => addShape('line')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><span style={{ width: 30, height: 8, background: PRIMARY, borderRadius: 2 }} /></div>
              <div className="pce-tile" onClick={() => addShape('star')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: PRIMARY, fontSize: 22 }}>★</div>
              <div className="pce-tile" onClick={() => addShape('stars')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: PRIMARY, fontSize: 14, letterSpacing: 1 }}>★★★</div>
              <div className="pce-tile" onClick={() => addShape('frame')} style={{ aspectRatio: '1', background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><span style={{ width: 26, height: 18, border: `2.5px solid ${PRIMARY}`, borderRadius: 4 }} /></div>
            </div>
            <div style={{ ...sectionLabel, marginTop: 4 }}>BRAND COLORS</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SWATCHES.map(c => (
                <span key={c} onClick={() => applySwatch(c)} title={selectedEls.length ? 'Apply to selection' : 'Select an element first'}
                  style={{ width: 30, height: 30, borderRadius: 8, background: c, border: `2px solid ${c === '#ffffff' ? '#e2e8f0' : 'transparent'}`, cursor: 'pointer', boxShadow: '0 1px 3px rgba(16,42,67,.15)' }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>Colors follow the party preference — click a swatch to recolor the selected element.</div>
          </>)}

          {tab === 'image' && (<>
            <div style={sectionLabel}>IMAGES</div>
            <div className="pce-tile" onClick={() => { imgTargetRef.current = null; imgInputRef.current?.click(); }}
              style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: '22px 14px', textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}>
              <span style={{ display: 'inline-flex', color: '#64748b' }}><Icon name="upload" size={22} /></span>
              <div style={{ fontSize: 13, fontWeight: 700, color: PRIMARY, marginTop: 6 }}>Upload image</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>PNG or JPEG</div>
            </div>
            <div style={panelCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: PRIMARY }}>
                <Icon name="user" size={16} /> Candidate photo
              </div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 5, lineHeight: 1.5 }}>
                {keepPhotoBg ? 'The photo is placed as-is, background included.' : 'The background is removed automatically and placed as a clean cutout.'}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#334155', marginTop: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={!keepPhotoBg} onChange={(e) => setKeepPhotoBg(!e.target.checked)} style={{ accentColor: PRIMARY }} />
                Remove background
              </label>
              <button className="pce-save" onClick={() => headInputRef.current?.click()} disabled={!!busyMsg}
                style={{ display: 'block', width: '100%', textAlign: 'center', background: PRIMARY, color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, padding: '8px 0', marginTop: 10, cursor: busyMsg ? 'wait' : 'pointer', opacity: busyMsg ? 0.65 : 1 }}>
                {busyMsg ? <><span className="pce-spin" />{keepPhotoBg ? 'Adding…' : 'Removing background…'}</> : 'Upload photo'}
              </button>
            </div>
            <div style={panelCard}>
              <div style={{ fontSize: 13, fontWeight: 700, color: PRIMARY }}>Background image</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 5, lineHeight: 1.5 }}>Upload a full-bleed image — it sits at the bottom as a locked layer.</div>
              <button className="pce-hbtn" onClick={() => bgInputRef.current?.click()}
                style={{ display: 'block', width: '100%', textAlign: 'center', background: 'transparent', border: `1px solid ${PRIMARY}`, color: PRIMARY, fontSize: 12, fontWeight: 600, borderRadius: 8, padding: '8px 0', marginTop: 10, cursor: 'pointer' }}>Upload</button>
              {bg?.type === 'image' && (
                <button className="pce-hbtn" onClick={() => setBgFill('#ffffff')}
                  style={{ display: 'block', width: '100%', textAlign: 'center', background: 'transparent', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: 12, fontWeight: 600, borderRadius: 8, padding: '7px 0', marginTop: 6, cursor: 'pointer' }}>Remove background image</button>
              )}
              {bg?.type === 'rect' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <input type="color" value={isHex(bg.fill) ? bg.fill : '#0d1826'} onChange={(e) => setBgFill(e.target.value)}
                    style={{ width: 30, height: 28, padding: 0, border: '1px solid #dee2e6', borderRadius: 6, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, color: '#64748b' }}>Background color</span>
                </div>
              )}
            </div>
          </>)}

          {tab === 'ai' && (<>
            {design.front.some(el => el.role === 'background' && el.type === 'image' && el.src) && (
              <>
                <div style={sectionLabel}>REFINE THIS DESIGN</div>
                <div style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 12, padding: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: '#1e3a8a', marginBottom: 6 }}>
                    Change the artwork without starting over — your text stays put.
                  </div>
                  <textarea className="pce-input" value={refinePrompt} onChange={(e) => setRefinePrompt(e.target.value)}
                    placeholder="e.g. warmer background, less busy on the left"
                    style={{ ...smallInput, minHeight: 52, resize: 'vertical', border: '1px solid #bfdbfe', fontFamily: 'inherit' }} />
                  <button className="pce-tile" onClick={runRefine} disabled={refineBusy || !refinePrompt.trim()}
                    style={{
                      marginTop: 8, width: '100%', padding: '9px 0', borderRadius: 9, cursor: refineBusy ? 'default' : 'pointer',
                      border: 'none', background: refineBusy || !refinePrompt.trim() ? '#93c5fd' : '#2563eb',
                      color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                    }}>
                    {refineBusy ? 'Refining…' : 'Refine artwork'}
                  </button>
                  {refineErr && <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 6 }}>{refineErr}</div>}
                </div>
              </>
            )}
            <div style={sectionLabel}>AI IMAGE GENERATION</div>
            <div style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>Describe the image</div>
              <textarea className="pce-input" value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)}
                placeholder="e.g. waving american flag at sunset"
                style={{ ...smallInput, minHeight: 52, resize: 'vertical', border: '1px solid #bbf7d0', fontFamily: 'inherit' }} />
              {/* size presets — mini shape icon previews the aspect ratio */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', margin: '8px 0 5px' }}>Size</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                {[
                  { id: '1024x1024', label: 'Square', dims: '1024×1024', iw: 20, ih: 20 },
                  { id: '1024x576', label: 'Wide', dims: '1024×576', iw: 26, ih: 15 },
                  { id: '576x1024', label: 'Tall', dims: '576×1024', iw: 15, ih: 26 },
                ].map(p => {
                  const active = genSize === p.id;
                  return (
                    <button key={p.id} className="pce-tile" onClick={() => setGenSize(p.id)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                        padding: '8px 2px 6px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${active ? ACCENT_GREEN : '#dbe7dd'}`,
                        background: active ? '#fff' : 'rgba(255,255,255,.6)',
                      }}>
                      <span style={{ height: 26, display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: p.iw, height: p.ih, border: `2px solid ${active ? ACCENT_GREEN : '#8494a5'}`, borderRadius: 4, display: 'block' }} />
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: active ? '#166534' : '#475569' }}>{p.label}</span>
                      <span style={{ fontSize: 9.5, color: '#94a3b8' }}>{p.dims}</span>
                    </button>
                  );
                })}
              </div>
              <button className="pce-genbtn" onClick={runGenerate} disabled={genBusy || !genPrompt.trim()}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', background: (genBusy || !genPrompt.trim()) ? '#94a3b8' : ACCENT_GREEN, color: '#fff', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 8, padding: '9px 0', marginTop: 8, cursor: (genBusy || !genPrompt.trim()) ? 'default' : 'pointer' }}>
                <Icon name="sparkles" size={15} /> {genBusy ? 'Generating…' : 'Generate'}
              </button>
            </div>
            {genErr && <div style={{ fontSize: 12, color: '#b91c1c' }}>{genErr}</div>}
            {genResult && (
              <div style={panelCard}>
                <img alt="Generated preview" src={`data:image/png;base64,${genResult}`}
                  style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  <button className="pce-hbtn" onClick={addGenerated} disabled={genBusy} style={{ ...panelBtn, marginBottom: 0 }}>Add to card</button>
                  <button className="pce-hbtn" onClick={addGeneratedCutout} disabled={genBusy} style={{ ...panelBtn, marginBottom: 0 }}>Add without background (PNG)</button>
                  <button className="pce-hbtn" onClick={() => setGenResult(null)} style={{ ...panelBtn, marginBottom: 0, color: '#64748b' }}>Discard</button>
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>Add the generated image as-is, or as a <b>background-removed PNG</b>.</div>
            {recents.length > 0 && (<>
              <div style={{ ...sectionLabel, marginTop: 4 }}>RECENT GENERATIONS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {recents.map((r, i) => (
                  <img key={i} alt={`Recent generation ${i + 1}`} src={`data:image/png;base64,${r.b64}`}
                    onClick={() => { setGenResult(r.b64); setGenResultSize(r.size); }} className="pce-tile"
                    style={{ width: '100%', aspectRatio: r.size === '1024x576' ? '16 / 9' : r.size === '576x1024' ? '9 / 16' : '1', objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: '1px solid #e5eaf0' }} />
                ))}
              </div>
            </>)}
          </>)}

          {tab === 'layers' && (<>
            <div style={sectionLabel}>LAYERS — {side.toUpperCase()}</div>
            <div style={{ border: '1px solid #e5eaf0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
              {[...els].reverse().map(el => (
                <LayerRow key={el.id} el={el} selected={selectedIds.includes(el.id)} dragOver={dragOverId === el.id}
                  onClick={(e) => {
                    const cur = selectedIdsRef.current;
                    if (e.metaKey || e.ctrlKey) setSelectedIds(cur.includes(el.id) ? cur.filter(i => i !== el.id) : [...cur, el.id]);
                    else setSelectedIds([el.id]);
                  }}
                  onHover={setHoverLayerId}
                  onToggleLock={toggleLock}
                  onDragStart={() => { dragIdRef.current = el.id; }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverId(el.id); }}
                  onDrop={() => reorderLayer(dragIdRef.current, el.id)}
                  onDragEnd={() => setDragOverId(null)} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>Click a row to select · drag to reorder · lock to protect</div>
          </>)}

          <div style={{ marginTop: 'auto', fontSize: 11, color: '#94a3b8', lineHeight: 1.7, borderTop: '1px solid #eef2f6', paddingTop: 10 }}>
            Double-click: edit text · ⌘-click: multi-select · Shift+drag: axis lock · ⌘D duplicate · ⌘Z undo · ⌘+scroll: zoom
          </div>

          {/* hidden file inputs */}
          <input ref={imgInputRef} type="file" accept="image/png,image/jpeg" onChange={onImageFile} style={{ display: 'none' }} />
          <input ref={headInputRef} type="file" accept="image/png,image/jpeg" onChange={onHeadshotFile} style={{ display: 'none' }} />
          <input ref={bgInputRef} type="file" accept="image/png,image/jpeg" onChange={onBgFile} style={{ display: 'none' }} />
        </div>

        {/* ══ worksurface ══ */}
        <div style={{ flex: 1, position: 'relative', minWidth: 0, background: '#e9eef4', backgroundImage: 'radial-gradient(rgba(16,42,67,0.09) 1px, transparent 1px)', backgroundSize: '18px 18px', overflow: 'hidden' }}>
          {/* scroller */}
          <div ref={stageWrapRef} style={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
            <div style={{ minWidth: '100%', minHeight: '100%', width: 'max-content', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '76px 44px 110px', boxSizing: 'border-box', margin: '0 auto' }}>
              {/* postcard canvas */}
              <div ref={stageBoxRef}
                style={{ width: CANVAS_W * scale, height: CANVAS_H * scale, position: 'relative', borderRadius: 4, boxShadow: '0 2px 6px rgba(16,42,67,.12), 0 18px 44px rgba(16,42,67,.26), 0 0 0 1px rgba(16,42,67,.06)', background: '#fff', overflow: 'hidden', touchAction: 'none', flexShrink: 0 }}
                onPointerDown={onStagePointerDown}>
                <div style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0, fontFamily: FONT }}>
                  {els.map(el => (
                    <ElView key={el.id} el={el} editing={editingId === el.id}
                      onPointerDown={onElPointerDown}
                      onDoubleClick={beginTextEdit}
                      onTextChange={(id, text) => patchEl(id, { text })}
                      onTextCommit={commitTextEdit} />
                  ))}

                  {/* bleed + safe-area print guides */}
                  {printGuides && (<>
                    <div style={{ position: 'absolute', inset: 6, border: `${1.5 / scale}px dashed rgba(244,63,94,.85)`, pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', inset: 34, border: `${1.5 / scale}px dashed rgba(45,212,191,.9)`, pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', left: 42, top: 40, fontSize: 11 / scale, fontWeight: 800, letterSpacing: '.1em', color: '#2dd4bf', background: 'rgba(10,20,32,.75)', padding: `${2 / scale}px ${7 / scale}px`, borderRadius: 4 / scale, pointerEvents: 'none' }}>SAFE AREA</div>
                    <div style={{ position: 'absolute', right: 14, top: 11, fontSize: 11 / scale, fontWeight: 800, letterSpacing: '.1em', color: GUIDE_PINK, background: 'rgba(10,20,32,.75)', padding: `${2 / scale}px ${7 / scale}px`, borderRadius: 4 / scale, pointerEvents: 'none' }}>BLEED</div>
                  </>)}

                  {/* hover highlight from the layers panel */}
                  {hoverEl && !selectedIds.includes(hoverEl.id) && (
                    <div style={{ position: 'absolute', left: hoverEl.x, top: hoverEl.y, width: hoverEl.w, height: hoverEl.h, border: `${bw}px dashed ${SEL_BLUE}`, opacity: 0.6, pointerEvents: 'none' }} />
                  )}

                  {/* selection chrome — outline every selected element (rotates with it) */}
                  {!editingId && selectedEls.map(el => (
                    <div key={`sel-${el.id}`} style={{ position: 'absolute', left: el.x, top: el.y, width: el.w, height: el.h, transform: el.rot ? `rotate(${el.rot}deg)` : undefined, pointerEvents: 'none' }}>
                      <div style={{ position: 'absolute', inset: 0, border: `${bw}px solid ${SEL_BLUE}` }} />
                      {single && !single.locked && el.id === single.id && HANDLES.map(hd => {
                        const p = handleXY(hd, el.w, el.h);
                        return (
                          <div key={hd} onPointerDown={(e) => startResize(e, hd)}
                            style={{ position: 'absolute', left: p.left, top: p.top, width: hs, height: hs, transform: 'translate(-50%,-50%)', background: '#fff', border: `${bw}px solid ${SEL_BLUE}`, borderRadius: 2 / scale, cursor: CURSORS[hd], pointerEvents: 'auto' }} />
                        );
                      })}
                      {single && !single.locked && el.id === single.id && (
                        <>
                          <div style={{ position: 'absolute', left: el.w / 2, top: el.h, width: 1 / scale, height: 18 / scale, background: SEL_BLUE, transform: 'translateX(-50%)' }} />
                          <div onPointerDown={startRotate} title="Rotate (Shift = 15° steps)"
                            style={{ position: 'absolute', left: el.w / 2, top: el.h + 24 / scale, width: 14 / scale, height: 14 / scale, transform: 'translate(-50%,-50%)', background: '#fff', border: `${bw}px solid ${SEL_BLUE}`, borderRadius: '50%', cursor: 'grab', pointerEvents: 'auto' }} />
                        </>
                      )}
                    </div>
                  ))}
                  {/* group bounding box for multi-select */}
                  {groupBox && !editingId && (
                    <div style={{ position: 'absolute', left: groupBox.x, top: groupBox.y, width: groupBox.w, height: groupBox.h, border: `${bw}px dashed ${SEL_BLUE}`, pointerEvents: 'none' }} />
                  )}

                  {/* snap guide lines */}
                  {guides.v != null && <div style={{ position: 'absolute', left: guides.v, top: 0, width: 1.5 / scale, height: CANVAS_H, background: GUIDE_PINK, pointerEvents: 'none' }} />}
                  {guides.h != null && <div style={{ position: 'absolute', top: guides.h, left: 0, height: 1.5 / scale, width: CANVAS_W, background: GUIDE_PINK, pointerEvents: 'none' }} />}

                  {/* marquee rectangle */}
                  {marquee && (
                    <div style={{ position: 'absolute', left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h, border: `${bw}px dashed ${SEL_BLUE}`, background: 'rgba(59,130,246,0.08)', pointerEvents: 'none' }} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* floating contextual toolbar */}
          {selectedEls.length > 0 && !editingId && (
            <div style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', background: '#fff', border: '1px solid #e5eaf0', borderRadius: 12, boxShadow: '0 8px 28px rgba(16,42,67,.16)', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', zIndex: 5, maxWidth: 'calc(100% - 40px)', flexWrap: 'wrap' }}>
              {single && single.type === 'text' && (<>
                <select className="pce-input" value={single.font || FONT} onFocus={pushHistory}
                  onChange={(e) => patchEl(single.id, { font: e.target.value })}
                  style={{ border: '1px solid #dfe6ee', borderRadius: 8, padding: '6px 8px', fontSize: 13, fontWeight: 600, color: PRIMARY, minWidth: 118, cursor: 'pointer', background: '#fff' }}>
                  {FONT_CHOICES.map(f => <option key={f.label} value={f.stack}>{f.label}</option>)}
                </select>
                <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #dfe6ee', borderRadius: 8, overflow: 'hidden', fontSize: 13, color: PRIMARY }}>
                  <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { size: Math.max(6, single.size - 2) }); }} style={{ border: 'none', background: 'transparent', padding: '6px 9px', color: '#64748b', cursor: 'pointer' }}>−</button>
                  <span style={{ padding: '6px 4px', fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{single.size}</span>
                  <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { size: single.size + 2 }); }} style={{ border: 'none', background: 'transparent', padding: '6px 9px', color: '#64748b', cursor: 'pointer' }}>＋</button>
                </span>
                <input type="color" value={isHex(single.color) ? single.color : '#000000'} onFocus={pushHistory}
                  onChange={(e) => patchEl(single.id, { color: e.target.value })} title="Text color"
                  style={{ width: 26, height: 26, padding: 0, border: '1px solid #dfe6ee', borderRadius: 6, cursor: 'pointer' }} />
                <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { weight: single.weight >= 700 ? 400 : 800 }); }}
                  style={{ width: 26, height: 26, border: 'none', background: 'transparent', borderRadius: 6, fontWeight: 900, fontSize: 14, color: single.weight >= 700 ? PRIMARY : '#94a3b8', cursor: 'pointer' }}>B</button>
                <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { italic: !single.italic }); }}
                  style={{ width: 22, height: 26, border: 'none', background: 'transparent', borderRadius: 6, fontStyle: 'italic', fontSize: 14, color: single.italic ? PRIMARY : '#94a3b8', cursor: 'pointer' }}>I</button>
                {toolDivider}
                {toolIcon('align-lines', cycleAlign, { title: `Align: ${single.align || 'left'}`, color: PRIMARY })}
                <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { upper: !single.upper }); }}
                  style={{ border: 'none', background: 'transparent', borderRadius: 6, fontSize: 12, fontWeight: 700, color: single.upper ? PRIMARY : '#94a3b8', cursor: 'pointer', padding: '4px 4px' }}>AA</button>
              </>)}

              {single && single.type === 'rect' && (<>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#8494a5', letterSpacing: '.06em' }}>SHAPE</span>
                <input type="color" value={isHex(single.fill) ? single.fill : '#000000'} onFocus={pushHistory}
                  onChange={(e) => patchEl(single.id, { fill: e.target.value })} title="Fill"
                  style={{ width: 26, height: 26, padding: 0, border: '1px solid #dfe6ee', borderRadius: 6, cursor: 'pointer' }} />
                <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #dfe6ee', borderRadius: 8, overflow: 'hidden', fontSize: 12, color: PRIMARY }}>
                  <span style={{ padding: '6px 8px', color: '#64748b' }}>Radius</span>
                  <input type="number" className="pce-input" min={0} value={single.r || 0} onFocus={pushHistory}
                    onChange={(e) => patchEl(single.id, { r: Math.max(0, parseFloat(e.target.value) || 0) })}
                    style={{ width: 46, border: 'none', padding: '6px 4px', fontSize: 12, fontWeight: 700 }} />
                </span>
              </>)}

              {single && single.type === 'image' && (<>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#8494a5', letterSpacing: '.06em' }}>IMAGE</span>
                <button className="pce-hbtn" onClick={() => { pushHistory(); patchEl(single.id, { fit: (single.fit || 'contain') === 'contain' ? 'cover' : 'contain' }); }}
                  style={{ border: '1px solid #dfe6ee', background: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: PRIMARY, cursor: 'pointer', textTransform: 'capitalize' }}>{single.fit || 'contain'}</button>
                <button className="pce-hbtn" onClick={() => { imgTargetRef.current = single.id; imgInputRef.current?.click(); }}
                  style={{ border: '1px solid #dfe6ee', background: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: PRIMARY, cursor: 'pointer' }}>Replace</button>
              </>)}

              {selectedEls.length > 1 && (<>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#8494a5', letterSpacing: '.06em' }}>{selectedEls.length} SELECTED</span>
                <button className="pce-hbtn" onClick={() => alignSel('left')} title="Align left" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>⇤</button>
                <button className="pce-hbtn" onClick={() => alignSel('hcenter')} title="Center horizontally" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>↔</button>
                <button className="pce-hbtn" onClick={() => alignSel('right')} title="Align right" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>⇥</button>
                <button className="pce-hbtn" onClick={() => alignSel('top')} title="Align top" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>⤒</button>
                <button className="pce-hbtn" onClick={() => alignSel('vcenter')} title="Center vertically" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>↕</button>
                <button className="pce-hbtn" onClick={() => alignSel('bottom')} title="Align bottom" style={{ ...zoomBtn, border: '1px solid #dfe6ee', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>⤓</button>
              </>)}

              {single?.locked && (
                <button className="pce-hbtn" onClick={() => toggleLock(single)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #dfe6ee', background: '#fff', borderRadius: 8, padding: '5px 9px', fontSize: 11, fontWeight: 600, color: PRIMARY, cursor: 'pointer' }}>
                  <Icon name="lock" size={11} /> Unlock
                </button>
              )}

              {toolDivider}
              {single && !single.locked && (<>
                {toolIcon('up', () => moveLayer(1), { title: 'Bring forward' })}
                {toolIcon('down', () => moveLayer(-1), { title: 'Send back' })}
              </>)}
              <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #dfe6ee', borderRadius: 8, overflow: 'hidden', fontSize: 11, color: '#64748b' }} title="Opacity">
                <input type="number" className="pce-input" min={0} max={100} value={Math.round(((single || selectedEls[0]).opacity ?? 1) * 100)} onFocus={pushHistory}
                  onChange={(e) => { const v = Math.min(1, Math.max(0, (parseFloat(e.target.value) || 0) / 100)); selectedEls.forEach(el => patchEl(el.id, { opacity: v })); }}
                  style={{ width: 40, border: 'none', padding: '6px 2px 6px 6px', fontSize: 12, fontWeight: 700, color: PRIMARY }} />
                <span style={{ paddingRight: 6 }}>%</span>
              </span>
              {toolIcon('copy', duplicateSelected, { title: 'Duplicate (⌘D)' })}
              {toolIcon('trash', removeSelected, { title: 'Delete', color: '#b91c1c' })}
            </div>
          )}

          {busyMsg && (
            <div style={{ position: 'absolute', top: selectedEls.length ? 66 : 16, left: '50%', transform: 'translateX(-50%)', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 10, padding: '8px 14px', fontSize: 13, zIndex: 6, boxShadow: '0 6px 20px rgba(16,42,67,.14)' }}><span className="pce-spin dark" />{busyMsg}</div>
          )}
          {errMsg && !busyMsg && (
            <div style={{ position: 'absolute', top: selectedEls.length ? 66 : 16, left: '50%', transform: 'translateX(-50%)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '8px 14px', fontSize: 13, zIndex: 6, boxShadow: '0 6px 20px rgba(67,16,16,.14)' }}>{errMsg}</div>
          )}

          {/* bottom bar: pages + zoom */}
          <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 16, zIndex: 5 }}>
            <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 12, boxShadow: '0 6px 20px rgba(16,42,67,.14)', display: 'flex', gap: 8, padding: 8 }}>
              {['front', 'back'].map(s => (
                <div key={s} onClick={() => { setSide(s); setSelectedIds([]); setEditingId(null); }}
                  style={{ width: 104, border: `2px solid ${side === s ? ACCENT_GREEN : '#e5eaf0'}`, borderRadius: 8, overflow: 'hidden', cursor: 'pointer' }}>
                  <MiniSide list={design[s]} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: PRIMARY, textAlign: 'center', padding: '3px 0', background: side === s ? '#f0fdf4' : '#fff', textTransform: 'capitalize' }}>{s}</div>
                </div>
              ))}
            </div>
            <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 10, boxShadow: '0 6px 20px rgba(16,42,67,.14)', display: 'flex', alignItems: 'center', fontSize: 13, color: PRIMARY }}>
              <button className="pce-hbtn" onClick={() => setZoom(z => Math.max(0.4, z / 1.25))} style={{ border: 'none', background: 'transparent', padding: '8px 12px', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>−</button>
              <span style={{ padding: '8px 4px', fontWeight: 700, fontSize: 12, minWidth: 38, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
              <button className="pce-hbtn" onClick={() => setZoom(z => Math.min(4, z * 1.25))} style={{ border: 'none', background: 'transparent', padding: '8px 12px', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>＋</button>
              <button className="pce-hbtn" onClick={() => setZoom(1)} style={{ border: 'none', background: 'transparent', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: PRIMARY, borderLeft: '1px solid #eef2f6', cursor: 'pointer' }}>Fit</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostcardEditor;
